import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter, useParams } from 'next/navigation';
import Image from 'next/image';
import { Check, X, Lock, ChevronDown, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { authFetch, isLoggedIn } from '@/lib/auth-client';
import { refreshBalance } from '@/lib/balance';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { scheduleUndercardNotifications } from '@/lib/notifications';
import { markFirstMilestoneOnce } from '@/lib/first-milestone';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { countryCode, countryDisplayName } from '@/lib/country-flags';
import { CountryPopover } from '@/components/ui/CountryPopover';
import type { UndercardDetail, UndercardSummary, LeaderboardEntry, GameData, PropData, GamePickData, MyPropData } from '@/services/events/main-event';
import { RoundStepper } from '@/components/events/RoundStepper';
import type { RoundStep } from '@/components/events/RoundStepper';
import { ShareCardButton } from '@/components/rewards/ShareCardButton';
import type { PickSelection } from '@prisma/client';
import { asset } from '@/lib/asset';

// ============================================================================
// COUNTDOWN TIMER
// ============================================================================

function CountdownTimer({ lockTime }: { lockTime: string | Date }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    function update() {
      const now = Date.now();
      const lock = new Date(lockTime).getTime();
      const diff = lock - now;

      if (diff <= 0) {
        setTimeLeft('Locked');
        setIsExpired(true);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    }

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [lockTime]);

  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-mono',
      isExpired
        ? 'bg-oxblood/10 border-oxblood/30 text-oxblood'
        : 'bg-gold/5 border-gold/20 text-gold'
    )}>
      {isExpired ? (
        <Lock className="w-3.5 h-3.5" />
      ) : (
        <Image src={asset('assets/icons/pocket_watch.webp')} alt="" width={16} height={16} />
      )}
      <span>{isExpired ? 'Picks locked' : `Locks in ${timeLeft}`}</span>
    </div>
  );
}

/** Format goal totals to max 3 decimal places, trimming trailing zeros */
function fmtPts(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}

// ============================================================================
// SCORE BANNER
// ============================================================================

function ScoreBanner({
  pickPoints, propBonusPoints, correctPicks, settledPicks, totalPicks,
  hitProps, settledMyProps, totalMyProps, isCompleted, accuracyModifier,
  pointsPerCorrectPick, totalScore,
}: {
  pickPoints: number;
  propBonusPoints: number;
  correctPicks: number;
  settledPicks: number;
  totalPicks: number;
  hitProps: number;
  settledMyProps: number;
  totalMyProps: number;
  isCompleted: boolean;
  accuracyModifier: number;
  pointsPerCorrectPick: number;
  totalScore: number;
}) {
  const displayScore = isCompleted ? totalScore : pickPoints + propBonusPoints;

  return (
    <div className={cn(
      'rounded-xl border p-3 mb-6',
      isCompleted
        ? 'bg-surface-2 border-verdigris/30'
        : 'bg-surface-2 border-gold/20',
    )}>
      {/* Main score row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-xl font-bold font-serif',
            isCompleted ? 'text-verdigris' : 'text-gold',
          )}>
            {fmtPts(displayScore)} points
          </span>
          {isCompleted && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-verdigris/70 bg-verdigris/10 px-2 py-0.5 rounded">
              Final
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-warm-gray">
          {settledPicks > 0 && (
            <span>Picks: <span className="text-cream font-medium">{correctPicks}/{settledPicks}</span></span>
          )}
          {settledMyProps > 0 && (
            <span>Props: <span className="text-cream font-medium">{hitProps}/{settledMyProps}</span></span>
          )}
          {settledPicks === 0 && settledMyProps === 0 && (
            <span className="text-warm-gray/60">Awaiting results</span>
          )}
        </div>
      </div>

      {/* Expanded breakdown on completion */}
      {isCompleted && (
        <div className="mt-2 pt-2 border-t border-surface-3 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-warm-gray">Pick points</span>
            <span className="text-cream">{fmtPts(pickPoints)} <span className="text-warm-gray/60">({correctPicks}/{totalPicks} × {pointsPerCorrectPick} points)</span></span>
          </div>
          {totalMyProps > 0 && (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-warm-gray">Prop bonus</span>
                <span className="text-cream">+{fmtPts(propBonusPoints)} <span className="text-warm-gray/60">({hitProps} hit)</span></span>
              </div>
              {accuracyModifier !== 1.0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-warm-gray">Accuracy modifier</span>
                  <span className={cn(
                    'font-medium',
                    accuracyModifier >= 1.0 ? 'text-verdigris' : 'text-oxblood',
                  )}>
                    ×{accuracyModifier.toFixed(1)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PURCHASED PROPS SUMMARY
// ============================================================================

function PurchasedPropTile({ prop }: { prop: MyPropData }) {
  const isHit = prop.result === 'HIT';
  const isMiss = prop.result === 'MISS';
  const isResolved = isHit || isMiss;

  // Extract a short label from description
  // "Lionel Messi — Goals O/U 1.5" → "Messi Goals 1.5"
  // "Detroit Pistons -5.5 (spread)" → "Pistons -5.5"
  // "76ers @ Pacers — Total O/U 220.5" → "Total O/U 220.5"
  const shortLabel = (() => {
    // Player prop
    const playerMatch = prop.description.match(/^(.+?)\s+[—–-]\s+(.+?)\s+O\/U\s+([\d.]+)$/i);
    if (playerMatch) {
      const lastName = playerMatch[1].split(' ').pop();
      const cat = playerMatch[2].replace('Points', 'Goals').replace('Rebounds', 'Reb').replace('Assists', 'Ast');
      return `${lastName} ${cat} ${playerMatch[3]}`;
    }
    // Spread
    const spreadMatch = prop.description.match(/^(.+?)\s+([+-]?[\d.]+)\s*\(spread\)$/i);
    if (spreadMatch) {
      const teamShort = spreadMatch[1].split(' ').pop();
      return `${teamShort} ${spreadMatch[2]}`;
    }
    // Total
    const totalMatch = prop.description.match(/Total\s+O\/U\s+([\d.]+)/i);
    if (totalMatch) return `Total O/U ${totalMatch[1]}`;
    // Moneyline
    const mlMatch = prop.description.match(/^(.+?)\s+wins/i);
    if (mlMatch) {
      const teamShort = mlMatch[1].split(' ').pop();
      return `${teamShort} ML`;
    }
    return prop.description.slice(0, 20);
  })();

  return (
    <div className={cn(
      'rounded-lg border px-2.5 py-1.5 text-[10px] leading-tight min-w-[100px]',
      isHit && 'bg-verdigris/10 border-verdigris/30',
      isMiss && 'bg-oxblood/10 border-oxblood/30',
      !isResolved && 'bg-gold/5 border-gold/20',
    )}>
      <div className={cn(
        'font-medium truncate',
        isHit && 'text-verdigris',
        isMiss && 'text-oxblood',
        !isResolved && 'text-gold',
      )}>
        {shortLabel}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className={cn(
          'font-bold text-[11px]',
          isHit && 'text-verdigris',
          isMiss && 'text-oxblood',
          !isResolved && 'text-warm-gray/60',
        )}>
          {isHit ? `HIT +${fmtPts(prop.bonusPoints)}` : isMiss ? 'MISS' : `${prop.bonusMultiplier}x`}
        </span>
        {!isResolved && (
          <span className="text-warm-gray/40">{prop.costCoins}c</span>
        )}
      </div>
    </div>
  );
}

function PurchasedPropsSummary({ myProps, purchasedCount, maxProps }: {
  myProps: MyPropData[];
  purchasedCount: number;
  maxProps: number;
}) {
  if (myProps.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase">
          My Prop Boosts
        </span>
        <span className="text-xs text-warm-gray">{purchasedCount}/{maxProps}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {myProps.map((prop) => (
          <PurchasedPropTile key={prop.undercardPropId} prop={prop} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// GAME PICK ROW
// ============================================================================

function GamePickRow({ game, pick, onPick, disabled }: {
  game: GameData;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  disabled: boolean;
}) {
  const settled = game.winner !== null;

  return (
    <div className={cn(
      'p-3 rounded-xl border transition-colors',
      settled ? 'bg-surface-2/50 border-surface-3' : 'bg-surface-2 border-surface-3',
    )}>
      <div className="flex items-center gap-2">
        {/* Away team button */}
        <button
          onClick={() => onPick(game.id, 'AWAY')}
          disabled={disabled || settled}
          className={cn(
            'flex-1 py-2.5 px-3 rounded-lg text-xs font-bold transition-all text-center',
            pick === 'AWAY'
              ? settled
                ? game.winner === 'AWAY' ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris' : 'bg-oxblood/20 border-2 border-oxblood text-oxblood'
                : 'bg-gold/20 border-2 border-gold text-gold'
              : 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4',
            (disabled || settled) && 'cursor-default'
          )}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <CountryFlag name={game.awayTeam} size="md" />
            <span>
              {game.awaySeed && <span className="text-warm-gray/60 mr-1">({game.awaySeed})</span>}
              {countryDisplayName(game.awayTeam)}
            </span>
          </span>
          {settled && game.awayScore !== null && (
            <span className="ml-1 text-xs opacity-70">({game.awayScore})</span>
          )}
        </button>

        {/* VS / result indicator */}
        <div className="w-8 text-center flex-shrink-0">
          {settled ? (
            pick ? (
              game.winner === pick ? (
                <Check className="w-4 h-4 text-verdigris mx-auto" />
              ) : (
                <X className="w-4 h-4 text-oxblood mx-auto" />
              )
            ) : (
              <span className="text-xs text-tertiary">—</span>
            )
          ) : (
            <span className="text-[10px] text-tertiary font-bold">VS</span>
          )}
        </div>

        {/* Home team button */}
        <button
          onClick={() => onPick(game.id, 'HOME')}
          disabled={disabled || settled}
          className={cn(
            'flex-1 py-2.5 px-3 rounded-lg text-xs font-bold transition-all text-center',
            pick === 'HOME'
              ? settled
                ? game.winner === 'HOME' ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris' : 'bg-oxblood/20 border-2 border-oxblood text-oxblood'
                : 'bg-gold/20 border-2 border-gold text-gold'
              : 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4',
            (disabled || settled) && 'cursor-default'
          )}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <CountryFlag name={game.homeTeam} size="md" />
            <span>
              {game.homeSeed && <span className="text-warm-gray/60 mr-1">({game.homeSeed})</span>}
              {countryDisplayName(game.homeTeam)}
            </span>
          </span>
          {settled && game.homeScore !== null && (
            <span className="ml-1 text-xs opacity-70">({game.homeScore})</span>
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// UNIFIED GAME CARD COMPONENTS
// ============================================================================

/** Small info tooltip that toggles on click */
function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-full border border-warm-gray/40 text-warm-gray/60 hover:text-warm-gray transition-colors"
        aria-label="Info"
      >
        <Info className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 px-3 py-2 rounded-lg bg-surface-3 border border-surface-4 text-[11px] text-cream/80 shadow-lg leading-snug">
          {text}
        </div>
      )}
    </div>
  );
}

/** Game Winner row — replaces GamePickRow inside the unified card */
function GameWinnerRow({ game, pick, onPick, disabled, pointsPerCorrectPick }: {
  game: GameData;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  disabled: boolean;
  pointsPerCorrectPick: number;
}) {
  const settled = game.winner !== null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Game Winner</span>
        <span className="text-[10px] text-warm-gray/40">({pointsPerCorrectPick} points if correct)</span>
      </div>
      <div className="flex flex-col @xs:flex-row @xs:items-center gap-2">
        {/* Away team */}
        <button
          onClick={() => onPick(game.id, 'AWAY')}
          disabled={disabled || settled}
          className={cn(
            'flex-1 py-2 px-2.5 rounded-lg text-xs font-bold transition-all text-center',
            pick === 'AWAY'
              ? settled
                ? game.winner === 'AWAY' ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris' : 'bg-oxblood/20 border-2 border-oxblood text-oxblood'
                : 'bg-gold/20 border-2 border-gold text-gold'
              : 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4',
            (disabled || settled) && 'cursor-default'
          )}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <CountryFlag name={game.awayTeam} size="md" />
            <span>
              {game.awaySeed && <span className="text-warm-gray/60 mr-1">({game.awaySeed})</span>}
              {countryDisplayName(game.awayTeam)}
            </span>
          </span>
          {settled && game.awayScore !== null && (
            <span className="ml-1 text-xs opacity-70">({game.awayScore})</span>
          )}
        </button>

        {/* VS / result */}
        <div className="w-8 text-center flex-shrink-0 @max-xs:hidden">
          {settled ? (
            pick ? (
              game.winner === pick ? (
                <Check className="w-4 h-4 text-verdigris mx-auto" />
              ) : (
                <X className="w-4 h-4 text-oxblood mx-auto" />
              )
            ) : (
              <span className="text-xs text-tertiary">—</span>
            )
          ) : (
            <span className="text-[10px] text-tertiary font-bold">or</span>
          )}
        </div>

        {/* Home team */}
        <button
          onClick={() => onPick(game.id, 'HOME')}
          disabled={disabled || settled}
          className={cn(
            'flex-1 py-2 px-2.5 rounded-lg text-xs font-bold transition-all text-center',
            pick === 'HOME'
              ? settled
                ? game.winner === 'HOME' ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris' : 'bg-oxblood/20 border-2 border-oxblood text-oxblood'
                : 'bg-gold/20 border-2 border-gold text-gold'
              : 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4',
            (disabled || settled) && 'cursor-default'
          )}
        >
          <span className="inline-flex items-center justify-center gap-1.5">
            <CountryFlag name={game.homeTeam} size="md" />
            <span>
              {game.homeSeed && <span className="text-warm-gray/60 mr-1">({game.homeSeed})</span>}
              {countryDisplayName(game.homeTeam)}
            </span>
          </span>
          {settled && game.homeScore !== null && (
            <span className="ml-1 text-xs opacity-70">({game.homeScore})</span>
          )}
        </button>
      </div>
    </div>
  );
}

/** Spread section — team toggle + single slider + buy */
function SpreadSection({ game, props: spreadProps, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  game: GameData;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const [team, setTeam] = useState<'AWAY' | 'HOME'>('AWAY');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, team]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  const selectedTeam = team === 'AWAY' ? game.awayTeam : game.homeTeam;
  const teamProps = spreadProps.filter((p) => p.description.includes(selectedTeam));
  const sorted = [...teamProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const withLines = sorted.filter((p) => p.line !== null);
  const displayProps = withLines.length > 0 ? withLines : sorted;

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const hasPurchased = spreadProps.some((p) => purchasedPropIds.has(p.id));
  const purchasedProp = hasPurchased ? spreadProps.find((p) => purchasedPropIds.has(p.id)) : null;
  const purchasedData = purchasedProp ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null : null;
  const isResolved = purchasedData?.result === 'HIT' || purchasedData?.result === 'MISS';
  const showSlider = new Set(displayProps.map((p) => p.line)).size >= 2;
  const purchasePending = purchasingPropId !== null;
  const isPurchasingCurrent = purchasingPropId === currentProp?.id;

  // Purchased state
  if (hasPurchased && purchasedProp) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Spread</span>
          <InfoTooltip text="Pick an alternate point spread — the further from the primary line, the bigger the payout." />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-warm-gray/60">{getAbbrev(purchasedProp.description.split(' ')[0])}</span>
          <span className="font-bold text-cream">{formatLine(purchasedProp.line)}</span>
          <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
          <span className={cn(
            'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
            isResolved
              ? purchasedData?.result === 'HIT' ? 'bg-verdigris/20 text-verdigris' : 'bg-oxblood/20 text-oxblood'
              : 'bg-gold/10 text-gold',
          )}>
            {isResolved
              ? (purchasedData?.result === 'HIT' ? `HIT +${fmtPts(purchasedData?.bonusPoints ?? 0)}` : 'MISS')
              : 'Purchased'}
          </span>
        </div>
      </div>
    );
  }

  // Locked, not purchased
  if (disabled) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Spread</span>
        </div>
        {currentProp && (
          <div className="flex items-center gap-2 text-[10px] text-warm-gray/40">
            <span>{getAbbrev(selectedTeam)}</span>
            <span>{formatLine(currentProp.line)}</span>
            <span>{currentProp.bonusMultiplier}x</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Spread</span>
        <InfoTooltip text="Pick an alternate point spread — the further from the primary line, the bigger the payout." />
      </div>

      {/* Team selector toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => { setTeam('AWAY'); setSliderIdx(-1); }}
          disabled={purchasePending}
          className={cn(
            'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
            team === 'AWAY'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >
          {getAbbrev(game.awayTeam)}
        </button>
        <button
          onClick={() => { setTeam('HOME'); setSliderIdx(-1); }}
          disabled={purchasePending}
          className={cn(
            'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
            team === 'HOME'
              ? 'bg-gold/20 border border-gold text-gold'
              : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
          )}
        >
          {getAbbrev(game.homeTeam)}
        </button>
      </div>

      {currentProp && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-cream whitespace-nowrap">{formatLine(currentProp.line)}</span>

          {showSlider && (
            <input
              type="range"
              min={0}
              max={displayProps.length - 1}
              value={Math.min(effectiveIndex, displayProps.length - 1)}
              onChange={(e) => setSliderIdx(Number(e.target.value))}
              disabled={purchasePending}
              className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer min-w-[60px]
                bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
                [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
                [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5
                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
                [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
            />
          )}

          <span className="text-[11px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>

          {isPurchasingCurrent ? (
            <Button variant="coins" size="sm" disabled>
              <span className="whitespace-nowrap">Buying...</span>
            </Button>
          ) : confirmPropId === currentProp.id ? (
            <button
              disabled={disabled || purchasePending}
              onClick={() => handleBuyClick(currentProp.id)}
              className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap"
            >
              Confirm {currentProp.costCoins}c
            </button>
          ) : (
            <Button
              variant="coins"
              size="sm"
              disabled={disabled || purchasePending}
              onClick={() => handleBuyClick(currentProp.id)}
            >
              <span className="flex items-center gap-1 whitespace-nowrap">
                Buy <Image src={asset('assets/icons/coins.webp')} alt="" width={12} height={12} /> {currentProp.costCoins}
              </span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Total Goals section — over/under toggle + slider + buy */
function TotalSection({ game, props: totalProps, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  game: GameData;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const [side, setSide] = useState<'OVER' | 'UNDER'>('OVER');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, side]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  // Determine side for each prop — supports both split (TOTAL_OVER/TOTAL_UNDER) and legacy (TOTAL) categories
  const getPropSide = (p: PropData): 'OVER' | 'UNDER' | null => {
    if (p.category === 'TOTAL_OVER') return 'OVER';
    if (p.category === 'TOTAL_UNDER') return 'UNDER';
    // Legacy TOTAL category — infer from description
    const descLower = p.description.toLowerCase();
    if (descLower.includes('over')) return 'OVER';
    if (descLower.includes('under')) return 'UNDER';
    return null;
  };

  const sideProps = totalProps.filter((p) => getPropSide(p) === side);
  // Show toggle if we can determine sides (either split categories or description-based)
  const hasOverUnder = totalProps.some((p) => getPropSide(p) !== null);
  const activeProps = hasOverUnder ? sideProps : totalProps;

  const sorted = [...activeProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const withLines = sorted.filter((p) => p.line !== null);
  const displayProps = withLines.length > 0 ? withLines : sorted;

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const hasPurchased = totalProps.some((p) => purchasedPropIds.has(p.id));
  const purchasedProp = hasPurchased ? totalProps.find((p) => purchasedPropIds.has(p.id)) : null;
  const purchasedData = purchasedProp ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null : null;
  const isResolved = purchasedData?.result === 'HIT' || purchasedData?.result === 'MISS';
  const showSlider = new Set(displayProps.map((p) => p.line)).size >= 2;
  const purchasePending = purchasingPropId !== null;
  const isPurchasingCurrent = purchasingPropId === currentProp?.id;

  // Purchased state
  if (hasPurchased && purchasedProp) {
    const purchasedSide = getPropSide(purchasedProp) === 'OVER' ? 'Over' : getPropSide(purchasedProp) === 'UNDER' ? 'Under' : '';
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Total Goals</span>
          <InfoTooltip text="Predict whether the combined score goes over or under the line." />
        </div>
        <div className="flex items-center gap-2 text-xs">
          {purchasedSide && <span className="text-warm-gray/60">{purchasedSide}</span>}
          <span className="font-bold text-cream">O/U {purchasedProp.line}</span>
          <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
          <span className={cn(
            'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
            isResolved
              ? purchasedData?.result === 'HIT' ? 'bg-verdigris/20 text-verdigris' : 'bg-oxblood/20 text-oxblood'
              : 'bg-gold/10 text-gold',
          )}>
            {isResolved
              ? (purchasedData?.result === 'HIT' ? `HIT +${fmtPts(purchasedData?.bonusPoints ?? 0)}` : 'MISS')
              : 'Purchased'}
          </span>
        </div>
      </div>
    );
  }

  // Locked, not purchased
  if (disabled) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Total Goals</span>
        </div>
        {currentProp && (
          <div className="flex items-center gap-2 text-[10px] text-warm-gray/40">
            <span>O/U {currentProp.line}</span>
            <span>{currentProp.bonusMultiplier}x</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Total Goals</span>
        <InfoTooltip text="Predict whether the combined score goes over or under the line." />
      </div>

      {/* Over/Under toggle */}
      {hasOverUnder && (
        <div className="flex gap-1">
          <button
            onClick={() => { setSide('OVER'); setSliderIdx(-1); }}
            disabled={purchasePending}
            className={cn(
              'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
              side === 'OVER'
                ? 'bg-gold/20 border border-gold text-gold'
                : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
            )}
          >
            Over
          </button>
          <button
            onClick={() => { setSide('UNDER'); setSliderIdx(-1); }}
            disabled={purchasePending}
            className={cn(
              'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
              side === 'UNDER'
                ? 'bg-gold/20 border border-gold text-gold'
                : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
            )}
          >
            Under
          </button>
        </div>
      )}

      {currentProp && (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-cream whitespace-nowrap">O/U {currentProp.line ?? '—'}</span>

            {showSlider && (
              <input
                type="range"
                min={0}
                max={displayProps.length - 1}
                value={Math.min(effectiveIndex, displayProps.length - 1)}
                onChange={(e) => setSliderIdx(Number(e.target.value))}
                disabled={purchasePending}
                className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer min-w-[60px]
                  bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
                  [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
                  [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5
                  [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
                  [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
              />
            )}

            <span className="text-[11px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>

            {isPurchasingCurrent ? (
              <Button variant="coins" size="sm" disabled>
                <span className="whitespace-nowrap">Buying...</span>
              </Button>
            ) : confirmPropId === currentProp.id ? (
              <button
                disabled={disabled || purchasePending}
                onClick={() => handleBuyClick(currentProp.id)}
                className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap"
              >
                Confirm {currentProp.costCoins}c
              </button>
            ) : (
              <Button
                variant="coins"
                size="sm"
                disabled={disabled || purchasePending}
                onClick={() => handleBuyClick(currentProp.id)}
              >
                <span className="flex items-center gap-1 whitespace-nowrap">
                  Buy <Image src={asset('assets/icons/coins.webp')} alt="" width={12} height={12} /> {currentProp.costCoins}
                </span>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Infer O/U side from player prop category or description */
function getPlayerPropSide(p: PropData): 'OVER' | 'UNDER' | null {
  if (p.category?.endsWith('_OVER')) return 'OVER';
  if (p.category?.endsWith('_UNDER')) return 'UNDER';
  const desc = p.description.toLowerCase();
  if (desc.includes(' over ')) return 'OVER';
  if (desc.includes(' under ')) return 'UNDER';
  if (desc.includes(' o/u ')) return null; // Legacy "O/U" — unsplit
  return null;
}

/** Get base category without _OVER/_UNDER suffix */
function getBaseCategory(category: string): string {
  return category.replace(/_OVER$/, '').replace(/_UNDER$/, '');
}

/** Single player prop category — O/U toggle + slider, inline in player card */
function PlayerCategoryRow({ category, props, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  category: string;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const [side, setSide] = useState<'OVER' | 'UNDER'>('OVER');
  const [sliderIdx, setSliderIdx] = useState(-1);
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIdx, side]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);

  // Check if props have O/U sides (split categories or description-based)
  const hasOverUnder = props.some((p) => getPlayerPropSide(p) !== null);
  const sideProps = hasOverUnder ? props.filter((p) => getPlayerPropSide(p) === side) : props;

  const sorted = [...sideProps].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  const displayProps = sorted.filter((p) => p.line !== null);
  const uniqueLines = new Set(displayProps.map((p) => p.line));
  const hasAltLines = uniqueLines.size >= 2;
  const isMainLineOnly = uniqueLines.size <= 1;

  const effectiveIndex = sliderIdx >= 0 && sliderIdx < displayProps.length
    ? sliderIdx
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const hasPurchased = props.some((p) => purchasedPropIds.has(p.id));
  const purchasedProp = hasPurchased ? props.find((p) => purchasedPropIds.has(p.id)) : null;
  const purchasedData = purchasedProp ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null : null;
  const isResolved = purchasedData?.result === 'HIT' || purchasedData?.result === 'MISS';
  const baseCategory = getBaseCategory(category);
  const label = CATEGORY_LABELS[baseCategory] || CATEGORY_LABELS[category] || category;
  const purchasePending = purchasingPropId !== null;
  const isPurchasingCurrent = purchasingPropId === currentProp?.id;

  if (!currentProp && !purchasedProp) return null;

  // Purchased state
  if (hasPurchased && purchasedProp) {
    const purchasedSide = getPlayerPropSide(purchasedProp);
    const sideLabel = purchasedSide === 'OVER' ? 'Over' : purchasedSide === 'UNDER' ? 'Under' : 'O/U';
    return (
      <div className="flex items-center gap-2 text-xs py-1.5">
        <span className="text-warm-gray/60 text-[10px] font-medium">{label}</span>
        <span className="text-warm-gray/50 text-[10px]">{sideLabel}</span>
        <span className="font-bold text-cream">{purchasedProp.line}</span>
        <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
        <span className={cn(
          'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
          isResolved
            ? purchasedData?.result === 'HIT' ? 'bg-verdigris/20 text-verdigris' : 'bg-oxblood/20 text-oxblood'
            : 'bg-gold/10 text-gold',
        )}>
          {isResolved
            ? (purchasedData?.result === 'HIT' ? `HIT +${fmtPts(purchasedData?.bonusPoints ?? 0)}` : 'MISS')
            : 'Purchased'}
        </span>
      </div>
    );
  }

  // Locked
  if (disabled) {
    return (
      <div className="flex items-center gap-2 text-[10px] text-warm-gray/40 py-1">
        <span className="font-medium">{label}</span>
        {currentProp && <><span>O/U {currentProp.line}</span><span>{currentProp.bonusMultiplier}x</span></>}
      </div>
    );
  }

  // Main line only — simplified: O/U toggle + direct buy
  if (isMainLineOnly) {
    return (
      <div className="py-1.5 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-warm-gray/60 font-medium">{label}</span>
          <span className="text-[10px] text-warm-gray/30 italic">main line</span>
        </div>
        <div className="flex items-center gap-1.5">
          {hasOverUnder && (
            <>
              <button disabled={purchasePending} onClick={() => setSide('OVER')} className={cn('px-2 py-0.5 rounded text-[10px] font-bold transition-all', side === 'OVER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>O</button>
              <button disabled={purchasePending} onClick={() => setSide('UNDER')} className={cn('px-2 py-0.5 rounded text-[10px] font-bold transition-all', side === 'UNDER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>U</button>
            </>
          )}
          <span className="text-xs font-bold text-cream">{currentProp.line}</span>
          <span className="text-[10px] text-warm-gray/50">{currentProp.bonusMultiplier}x</span>
          <span className="flex-1" />
          {isPurchasingCurrent ? (
            <Button variant="coins" size="sm" disabled>
              <span className="whitespace-nowrap text-[10px]">Buying...</span>
            </Button>
          ) : confirmPropId === currentProp.id ? (
            <button onClick={() => handleBuyClick(currentProp.id)}
              disabled={purchasePending}
              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap">
              Confirm {currentProp.costCoins}c
            </button>
          ) : (
            <Button variant="coins" size="sm" disabled={disabled || purchasePending} onClick={() => handleBuyClick(currentProp.id)}>
              <span className="flex items-center gap-1 whitespace-nowrap text-[10px]">
                <Image src={asset('assets/icons/coins.webp')} alt="" width={10} height={10} /> {currentProp.costCoins}
              </span>
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Alt lines — O/U toggle + slider
  return (
    <div className="py-1.5 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-warm-gray/60 font-medium">{label}</span>
      </div>
      {hasOverUnder && (
        <div className="flex gap-1">
          <button disabled={purchasePending} onClick={() => { setSide('OVER'); setSliderIdx(-1); }} className={cn('flex-1 py-0.5 px-2 rounded text-[10px] font-bold transition-all', side === 'OVER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>Over</button>
          <button disabled={purchasePending} onClick={() => { setSide('UNDER'); setSliderIdx(-1); }} className={cn('flex-1 py-0.5 px-2 rounded text-[10px] font-bold transition-all', side === 'UNDER' ? 'bg-gold/20 border border-gold text-gold' : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4')}>Under</button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-cream whitespace-nowrap">{currentProp.line ?? '—'}</span>
        {hasAltLines && (
          <input
            type="range"
            min={0}
            max={displayProps.length - 1}
            value={Math.min(effectiveIndex, displayProps.length - 1)}
            onChange={(e) => setSliderIdx(Number(e.target.value))}
            disabled={purchasePending}
            className="flex-1 h-1 rounded-full appearance-none cursor-pointer min-w-[40px]
              bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
              [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3
              [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
              [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
          />
        )}
        <span className="text-[10px] text-warm-gray/50 whitespace-nowrap">{currentProp.bonusMultiplier}x</span>
        {isPurchasingCurrent ? (
          <Button variant="coins" size="sm" disabled>
            <span className="whitespace-nowrap text-[10px]">Buying...</span>
          </Button>
        ) : confirmPropId === currentProp.id ? (
          <button onClick={() => handleBuyClick(currentProp.id)}
            disabled={purchasePending}
            className="px-2 py-0.5 rounded text-[10px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors whitespace-nowrap">
            Confirm {currentProp.costCoins}c
          </button>
        ) : (
          <Button variant="coins" size="sm" disabled={disabled || purchasePending} onClick={() => handleBuyClick(currentProp.id)}>
            <span className="flex items-center gap-1 whitespace-nowrap text-[10px]">
              <Image src={asset('assets/icons/coins.webp')} alt="" width={10} height={10} /> {currentProp.costCoins}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
}

/** Collapsible player row inside game card — name + category rows */
function InlinePlayerCard({ playerName, props, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  playerName: string;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const purchasedCount = props.filter((p) => purchasedPropIds.has(p.id)).length;

  // Group by base category (merge PLAYER_POINTS_OVER + PLAYER_POINTS_UNDER → PLAYER_POINTS)
  const byCategory = new Map<string, PropData[]>();
  for (const prop of props) {
    const baseCat = getBaseCategory(prop.category || 'OTHER');
    const arr = byCategory.get(baseCat) || [];
    arr.push(prop);
    byCategory.set(baseCat, arr);
  }

  const categoryOrder = [
    'PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS',
    'PLAYER_POINTS_REBOUNDS', 'PLAYER_POINTS_ASSISTS', 'PLAYER_REBOUNDS_ASSISTS',
    'OTHER',
  ];
  const sortedCategories = [...byCategory.entries()].sort(
    (a, b) => {
      const ai = categoryOrder.indexOf(a[0]);
      const bi = categoryOrder.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    }
  );

  const parts = playerName.split(' ');
  const displayName = parts.length >= 2
    ? `${parts.slice(-1)[0]}, ${parts.slice(0, -1).join(' ')}`
    : playerName;

  const pointsProp = props.find((p) => p.category === 'PLAYER_POINTS');
  const previewLine = pointsProp ? `${pointsProp.line} goals` : `${sortedCategories.length} cats`;

  return (
    <div className="border-b border-surface-3/30 last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-surface-3/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={cn('w-2.5 h-2.5 text-warm-gray/40 transition-transform', !expanded && '-rotate-90')} />
          <span className="text-cream text-[11px] font-medium">{displayName}</span>
          <span className="text-[10px] text-warm-gray/40">{previewLine}</span>
        </div>
        <div className="flex items-center gap-2">
          {purchasedCount > 0 && (
            <span className="text-[10px] text-gold font-medium">{purchasedCount} bought</span>
          )}
          <span className="text-[10px] text-warm-gray/30">{sortedCategories.length}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-2">
          {sortedCategories.map(([category, catProps]) => (
            <PlayerCategoryRow
              key={category}
              category={category}
              props={catProps}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              onPurchase={onPurchase}
              disabled={disabled}
              purchasingPropId={purchasingPropId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Unified game card — combines winner pick + spread + total + player props */
export function UnifiedGameCard({ game, pick, onPick, picksDisabled, pointsPerCorrectPick, props: gameProps, playerProps, manualProps: manualGameProps, myProps, purchasedPropIds, onPurchase, propDisabled, purchasingPropId }: {
  game: GameData;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  picksDisabled: boolean;
  pointsPerCorrectPick: number;
  props: PropData[];
  playerProps: PropData[];
  manualProps: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string, userSelection?: 'YES' | 'NO') => void;
  propDisabled: boolean;
  purchasingPropId: string | null;
}) {
  const [playerPropsExpanded, setPlayerPropsExpanded] = useState(false);

  // Filter out MONEYLINE (replaced by Game Winner) and separate SPREAD vs TOTAL
  const spreadProps = gameProps.filter((p) => p.category === 'SPREAD');
  const totalProps = gameProps.filter((p) => p.category === 'TOTAL_OVER' || p.category === 'TOTAL_UNDER' || p.category === 'TOTAL');

  // Group player props by player
  const byPlayer = new Map<string, PropData[]>();
  for (const prop of playerProps) {
    const name = extractPlayerName(prop.description);
    const arr = byPlayer.get(name) || [];
    arr.push(prop);
    byPlayer.set(name, arr);
  }
  const sortedPlayers = [...byPlayer.entries()].sort((a, b) => {
    const aLine = a[1].find((p) => p.category === 'PLAYER_POINTS')?.line ?? 0;
    const bLine = b[1].find((p) => p.category === 'PLAYER_POINTS')?.line ?? 0;
    return bLine - aLine;
  });
  const playerPropPurchasedCount = playerProps.filter((p) => purchasedPropIds.has(p.id)).length;

  const gameDate = new Date(game.gameTime);
  const timeStr = gameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    + ' \u2022 ' + gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="@container rounded-xl border border-surface-3 bg-surface-2 overflow-hidden w-full">
      {/* Game header bar */}
      <div className="px-4 py-2.5 bg-surface-3/50 border-b border-surface-3">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 text-sm font-bold text-cream min-w-0 @max-xs:flex-col @max-xs:items-start @max-xs:gap-0.5">
            <CountryPopover name={game.awayTeam}>
              <CountryFlag name={game.awayTeam} size="md" variant="emoji" />
              {game.awaySeed && <span className="text-warm-gray/50 text-xs mr-0.5">({game.awaySeed})</span>}
              {getAbbrev(game.awayTeam)}
            </CountryPopover>
            <span className="text-warm-gray/40 text-xs font-normal @max-xs:hidden">@</span>
            <CountryPopover name={game.homeTeam}>
              <CountryFlag name={game.homeTeam} size="md" variant="emoji" />
              {game.homeSeed && <span className="text-warm-gray/50 text-xs mr-0.5">({game.homeSeed})</span>}
              {getAbbrev(game.homeTeam)}
            </CountryPopover>
          </div>
          {game.winner !== null ? (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-verdigris bg-verdigris/10 border border-verdigris/30 shrink-0">
              Completed
            </span>
          ) : picksDisabled ? (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-gold bg-gold/10 border border-gold/30 shrink-0">
              <Lock className="w-2.5 h-2.5" />
              Locked
            </span>
          ) : (
            <span className="text-[10px] text-warm-gray/50 shrink-0 @max-xs:hidden">{timeStr}</span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
      <GameWinnerRow
        game={game}
        pick={pick}
        onPick={onPick}
        disabled={picksDisabled}
        pointsPerCorrectPick={pointsPerCorrectPick}
      />
      {spreadProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <SpreadSection
            game={game}
            props={spreadProps}
            myProps={myProps}
            purchasedPropIds={purchasedPropIds}
            onPurchase={onPurchase}
            disabled={propDisabled}
            purchasingPropId={purchasingPropId}
          />
        </>
      )}
      {totalProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <TotalSection
            game={game}
            props={totalProps}
            myProps={myProps}
            purchasedPropIds={purchasedPropIds}
            onPurchase={onPurchase}
            disabled={propDisabled}
            purchasingPropId={purchasingPropId}
          />
        </>
      )}
      {manualGameProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <div className="-mx-2 space-y-0.5">
            <div className="px-2 mb-1 text-[10px] text-warm-gray/60 font-medium uppercase tracking-wider">
              Predictions
            </div>
            {manualGameProps.map((prop) => (
              <InlineManualPropRow
                key={prop.id}
                prop={prop}
                gameSettled={game.winner != null}
                purchased={myProps.find((m) => m.undercardPropId === prop.id) ?? null}
                onPurchase={onPurchase}
                disabled={propDisabled}
                purchasingPropId={purchasingPropId}
              />
            ))}
          </div>
        </>
      )}
      </div>

      {/* Player Props — collapsible section at bottom */}
      {sortedPlayers.length > 0 && (
        <div className="border-t border-surface-3">
          <button
            onClick={() => setPlayerPropsExpanded(!playerPropsExpanded)}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-surface-3/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ChevronDown className={cn('w-3.5 h-3.5 text-warm-gray/40 transition-transform', !playerPropsExpanded && '-rotate-90')} />
              <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Player Props</span>
              <span className="text-[10px] text-warm-gray/40">{sortedPlayers.length} players</span>
            </div>
            <div className="flex items-center gap-2">
              {playerPropPurchasedCount > 0 && (
                <span className="text-[10px] text-gold font-medium">{playerPropPurchasedCount} bought</span>
              )}
            </div>
          </button>
          {playerPropsExpanded && (
            <div>
              {sortedPlayers.map(([playerName, pProps]) => (
                <InlinePlayerCard
                  key={playerName}
                  playerName={playerName}
                  props={pProps}
                  myProps={myProps}
                  purchasedPropIds={purchasedPropIds}
                  onPurchase={onPurchase}
                  disabled={propDisabled}
                  purchasingPropId={purchasingPropId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Horizontal scroll-snap track of match cards with a per-match dot stepper
 * above it. The active dot tracks the card nearest the left edge as you scroll,
 * and tapping a dot jumps to that match. Mirrors the swipe-picks MatchDeck idiom.
 */
function MatchCarousel({
  games,
  renderCard,
}: {
  games: GameData[];
  renderCard: (game: GameData) => React.ReactNode;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  // Index of the card nearest the track's left edge (accounting for px-4 inset).
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const left = track.scrollLeft + 16;
    let best = 0;
    let bestDist = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const d = Math.abs((child as HTMLElement).offsetLeft - left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  };

  const goTo = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const el = track.children[i] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  if (games.length === 0) return null;

  return (
    <div>
      {/* Per-match dot stepper */}
      {games.length > 1 && (
        <div className="flex items-center gap-1.5 mb-3">
          {games.map((game, i) => (
            <button
              key={game.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to match ${i + 1}`}
              aria-current={i === active}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === active ? 'w-6 bg-gold' : 'w-1.5 bg-surface-4 hover:bg-warm-gray/50',
              )}
            />
          ))}
        </div>
      )}

      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6 pb-2"
        style={{ scrollPaddingLeft: '1rem' }}
      >
        {games.map(renderCard)}
      </div>
    </div>
  );
}

// ============================================================================
// PROP COMPONENTS
// ============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  SPREAD: 'Spread',
  TOTAL: 'Game Total',
  TOTAL_OVER: 'Total Over',
  TOTAL_UNDER: 'Total Under',
  TEAM_TOTAL: 'Team Total',
  TEAM_TOTAL_OVER: 'Team Total Over',
  TEAM_TOTAL_UNDER: 'Team Total Under',
  MONEYLINE: 'Moneyline',
  PLAYER_POINTS: 'Points',
  PLAYER_REBOUNDS: 'Rebounds',
  PLAYER_ASSISTS: 'Assists',
  PLAYER_POINTS_REBOUNDS: 'Pts+Reb',
  PLAYER_POINTS_ASSISTS: 'Pts+Ast',
  PLAYER_REBOUNDS_ASSISTS: 'Reb+Ast',
};

/** Format a line value with +/- sign */
function formatLine(line: number | null): string {
  if (line === null) return '—';
  return line > 0 ? `+${line}` : `${line}`;
}

/** Derive a short abbreviation from a team name (e.g. "Michigan St Spartans" → "MSU") */
const TEAM_ABBREV_MAP: Record<string, string> = {
  // Big Ten
  'michigan state': 'MSU', 'michigan st': 'MSU', 'michigan st spartans': 'MSU', 'michigan state spartans': 'MSU',
  'michigan': 'MICH', 'michigan wolverines': 'MICH',
  'ohio state': 'OSU', 'ohio st': 'OSU', 'ohio state buckeyes': 'OSU', 'ohio st buckeyes': 'OSU',
  'penn state': 'PSU', 'penn st': 'PSU', 'penn state nittany lions': 'PSU',
  'iowa': 'IOWA', 'iowa hawkeyes': 'IOWA',
  'iowa state': 'ISU', 'iowa st': 'ISU', 'iowa state cyclones': 'ISU', 'iowa st cyclones': 'ISU',
  'illinois': 'ILL', 'illinois fighting illini': 'ILL',
  'purdue': 'PUR', 'purdue boilermakers': 'PUR',
  'indiana': 'IND', 'indiana hoosiers': 'IND',
  'wisconsin': 'WIS', 'wisconsin badgers': 'WIS',
  'minnesota': 'MINN', 'minnesota golden gophers': 'MINN',
  'northwestern': 'NW', 'northwestern wildcats': 'NW',
  'nebraska': 'NEB', 'nebraska cornhuskers': 'NEB',
  'maryland': 'MD', 'maryland terrapins': 'MD',
  'rutgers': 'RUT', 'rutgers scarlet knights': 'RUT',
  // ACC
  'duke': 'DUKE', 'duke blue devils': 'DUKE',
  'north carolina': 'UNC', 'north carolina tar heels': 'UNC',
  'nc state': 'NCST', 'north carolina state': 'NCST', 'nc state wolfpack': 'NCST',
  'virginia': 'UVA', 'virginia cavaliers': 'UVA',
  'virginia tech': 'VT', 'virginia tech hokies': 'VT',
  'clemson': 'CLEM', 'clemson tigers': 'CLEM',
  'louisville': 'LOU', 'louisville cardinals': 'LOU',
  'pittsburgh': 'PITT', 'pittsburgh panthers': 'PITT',
  'wake forest': 'WAKE', 'wake forest demon deacons': 'WAKE',
  'syracuse': 'SYR', 'syracuse orange': 'SYR',
  'florida state': 'FSU', 'florida st': 'FSU', 'florida state seminoles': 'FSU',
  'georgia tech': 'GT', 'georgia tech yellow jackets': 'GT',
  'notre dame': 'ND', 'notre dame fighting irish': 'ND',
  // SEC
  'alabama': 'BAMA', 'alabama crimson tide': 'BAMA',
  'auburn': 'AUB', 'auburn tigers': 'AUB',
  'tennessee': 'TENN', 'tennessee volunteers': 'TENN',
  'kentucky': 'UK', 'kentucky wildcats': 'UK',
  'florida': 'FLA', 'florida gators': 'FLA',
  'georgia': 'UGA', 'georgia bulldogs': 'UGA',
  'arkansas': 'ARK', 'arkansas razorbacks': 'ARK',
  'mississippi state': 'MSST', 'mississippi st': 'MSST', 'mississippi state bulldogs': 'MSST',
  'ole miss': 'MISS', 'ole miss rebels': 'MISS', 'mississippi': 'MISS',
  'lsu': 'LSU', 'lsu tigers': 'LSU',
  'south carolina': 'SC', 'south carolina gamecocks': 'SC',
  'texas a&m': 'TAMU', 'texas am': 'TAMU', 'texas a&m aggies': 'TAMU',
  'missouri': 'MIZ', 'missouri tigers': 'MIZ',
  'vanderbilt': 'VAN', 'vanderbilt commodores': 'VAN',
  // Big 12
  'kansas': 'KU', 'kansas jayhawks': 'KU',
  'kansas state': 'KSU', 'kansas st': 'KSU', 'kansas state wildcats': 'KSU',
  'baylor': 'BAY', 'baylor bears': 'BAY',
  'texas tech': 'TTU', 'texas tech red raiders': 'TTU',
  'oklahoma': 'OU', 'oklahoma sooners': 'OU',
  'oklahoma state': 'OKST', 'oklahoma st': 'OKST', 'oklahoma state cowboys': 'OKST',
  'west virginia': 'WVU', 'west virginia mountaineers': 'WVU',
  'tcu': 'TCU', 'tcu horned frogs': 'TCU',
  'texas': 'TEX', 'texas longhorns': 'TEX',
  'cincinnati': 'CIN', 'cincinnati bearcats': 'CIN',
  'houston': 'HOU', 'houston cougars': 'HOU',
  'byu': 'BYU', 'byu cougars': 'BYU',
  'ucf': 'UCF', 'ucf knights': 'UCF',
  'colorado': 'COL', 'colorado buffaloes': 'COL',
  'arizona': 'ARIZ', 'arizona wildcats': 'ARIZ',
  'arizona state': 'ASU', 'arizona st': 'ASU', 'arizona state sun devils': 'ASU',
  'utah': 'UTAH', 'utah utes': 'UTAH',
  // Pac-12 remnants / WCC / other
  'gonzaga': 'GONZ', 'gonzaga bulldogs': 'GONZ',
  'uconn': 'UCONN', 'uconn huskies': 'UCONN', 'connecticut': 'UCONN', 'connecticut huskies': 'UCONN',
  'creighton': 'CREI', 'creighton bluejays': 'CREI',
  'marquette': 'MARQ', 'marquette golden eagles': 'MARQ',
  'villanova': 'NOVA', 'villanova wildcats': 'NOVA',
  'xavier': 'XAV', 'xavier musketeers': 'XAV',
  'seton hall': 'SH', 'seton hall pirates': 'SH',
  'st johns': 'STJ', "st john's": 'STJ', "st. john's": 'STJ',
  'san diego state': 'SDSU', 'san diego st': 'SDSU',
  'memphis': 'MEM', 'memphis tigers': 'MEM',
  'usc': 'USC', 'usc trojans': 'USC',
  'ucla': 'UCLA', 'ucla bruins': 'UCLA',
  'oregon': 'ORE', 'oregon ducks': 'ORE',
  'washington': 'WASH', 'washington huskies': 'WASH',
};

function getAbbrev(teamName: string): string {
  // Strip seed prefix like "(3) "
  const clean = teamName.replace(/^\(\d+\)\s*/, '').trim();
  const key = clean.toLowerCase();

  // Countries (e.g. World Cup teams) use the standard FIFA 3-letter code
  // ("Czechia" → "CZE", "South Korea" → "KOR", "South Africa" → "RSA").
  const country = countryCode(clean);
  if (country) return country;

  // Exact match
  if (TEAM_ABBREV_MAP[key]) return TEAM_ABBREV_MAP[key];

  // Try without trailing mascot (e.g., "Michigan State Spartans" → "michigan state")
  const words = clean.split(' ');
  for (let i = words.length - 1; i >= 1; i--) {
    const partial = words.slice(0, i).join(' ').toLowerCase();
    if (TEAM_ABBREV_MAP[partial]) return TEAM_ABBREV_MAP[partial];
  }

  // Fallback: first word, max 4 chars uppercase
  return (words.length === 1 ? words[0] : words[0].slice(0, 4)).toUpperCase();
}

/** Slider-based category group — slide to pick a line, then Buy */
function CategorySliderGroup({ category, props, myProps, purchasedPropIds, sliderIndex, onSlide, onPurchase, disabled, game, spreadTeam, onSpreadTeamChange, conflicted }: {
  category: string;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  sliderIndex: number;
  onSlide: (category: string, index: number) => void;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  game?: GameData;
  spreadTeam?: 'home' | 'away';
  onSpreadTeamChange?: (team: 'home' | 'away') => void;
  conflicted?: boolean;
}) {
  // Two-tap buy: first tap → confirm state, second tap → purchase, auto-revert after 3s
  const [confirmPropId, setConfirmPropId] = useState<string | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBuyClick = useCallback((propId: string) => {
    if (confirmPropId === propId) {
      // Second tap — purchase
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(null);
      onPurchase(propId);
    } else {
      // First tap — enter confirm state
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      setConfirmPropId(propId);
      confirmTimerRef.current = setTimeout(() => setConfirmPropId(null), 3000);
    }
  }, [confirmPropId, onPurchase]);

  // Reset confirm when slider moves
  useEffect(() => {
    setConfirmPropId(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, [sliderIndex]);
  useEffect(() => {
    return () => { if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); };
  }, []);
  const sorted = [...props].sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  // Filter out props with null lines (old data) for the slider
  const withLines = sorted.filter((p) => p.line !== null);
  let displayProps = withLines.length > 0 ? withLines : sorted;

  const isSpread = category === 'SPREAD';
  const isTotal = category === 'TOTAL';
  const isAway = spreadTeam === 'away';

  // For spreads: filter to only show props for the selected team
  // Props are stored separately for each team (e.g., "Grizzlies -5.5 (spread)" vs "Pistons +5.5 (spread)")
  if (isSpread && game) {
    const teamName = isAway ? game.awayTeam : game.homeTeam;
    const teamFiltered = displayProps.filter((p) => p.description.includes(teamName));
    if (teamFiltered.length > 0) displayProps = teamFiltered;
  }

  // Default to the primary line (closest to 1.0x multiplier) when no explicit position set
  const effectiveIndex = sliderIndex >= 0 && sliderIndex < displayProps.length
    ? sliderIndex
    : displayProps.reduce((bestIdx, p, i, arr) => {
        const bestDist = Math.abs((arr[bestIdx]?.bonusMultiplier ?? 99) - 1.0);
        const curDist = Math.abs(p.bonusMultiplier - 1.0);
        return curDist < bestDist ? i : bestIdx;
      }, 0);

  const currentProp = displayProps[effectiveIndex] || displayProps[0];
  const hasPurchased = displayProps.some((p) => purchasedPropIds.has(p.id));
  const purchasedProp = hasPurchased
    ? displayProps.find((p) => purchasedPropIds.has(p.id))
    : null;
  const purchasedData = purchasedProp
    ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null
    : null;
  const isResolved = purchasedData?.result === 'HIT' || purchasedData?.result === 'MISS';
  // Only show slider if we have 2+ distinct line values
  const uniqueLines = new Set(displayProps.map((p) => p.line));
  const isSingleLine = uniqueLines.size <= 1;

  // For spreads: display the line with +/- sign (no negation needed — already stored per team)
  const displayCurrentLine = (line: number | null) => {
    if (line === null) return '—';
    return formatLine(line);
  };

  // Team label for spreads
  const spreadTeamName = isSpread && game
    ? (isAway ? game.awayTeam : game.homeTeam)
    : null;

  // Conflicted: Spread/Moneyline mutual exclusion
  if (conflicted && !hasPurchased) {
    const conflictWith = category === 'SPREAD' ? 'Moneyline' : 'Spread';
    return (
      <div className="rounded-lg border border-surface-3/30 bg-surface-2/20 px-2.5 py-1.5 flex items-center justify-between opacity-50">
        <span className="text-[10px] text-warm-gray/40 font-medium uppercase tracking-wider">
          {CATEGORY_LABELS[category] || category}
        </span>
        <span className="text-[10px] text-warm-gray/30 italic">
          Conflicts with {conflictWith}
        </span>
      </div>
    );
  }

  // Condensed greyed-out tile for non-purchased props when locked
  if (disabled && !hasPurchased) {
    const primaryProp = currentProp || displayProps[0];
    if (!primaryProp) return null;
    return (
      <div className="rounded-lg border border-surface-3/50 bg-surface-2/30 px-2.5 py-1.5 flex items-center justify-between">
        <span className="text-[10px] text-warm-gray/40 font-medium uppercase tracking-wider">
          {CATEGORY_LABELS[category] || category}
        </span>
        <span className="text-[10px] text-warm-gray/30">
          {isTotal ? `O/U ${primaryProp.line}` : formatLine(primaryProp.line)} · {primaryProp.bonusMultiplier}x
        </span>
      </div>
    );
  }

  return (
    <div className={cn(
      'rounded-lg border p-2.5 transition-all',
      hasPurchased ? 'bg-surface-2/50 border-gold/30' : 'bg-surface-2 border-surface-3',
    )}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">
          {CATEGORY_LABELS[category] || category}
        </p>
        {hasPurchased && purchasedProp && (
          <span className={cn(
            'text-xs font-bold px-2 py-0.5 rounded',
            isResolved
              ? purchasedData?.result === 'HIT'
                ? 'bg-verdigris/20 text-verdigris'
                : 'bg-oxblood/20 text-oxblood'
              : 'bg-gold/10 text-gold',
          )}>
            {isResolved
              ? (purchasedData?.result === 'HIT' ? `HIT +${fmtPts(purchasedData?.bonusPoints ?? 0)}` : 'MISS')
              : `${formatLine(purchasedProp.line)} · ${purchasedProp.bonusMultiplier}x`}
          </span>
        )}
      </div>

      {/* Spread team picker */}
      {isSpread && game && !hasPurchased && onSpreadTeamChange && (
        <div className="flex gap-1 mb-1.5">
          <button
            onClick={() => onSpreadTeamChange('away')}
            className={cn(
              'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
              isAway
                ? 'bg-gold/20 border border-gold text-gold'
                : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
            )}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              <CountryFlag name={game.awayTeam} size="sm" />
              {game.awayTeam}
            </span>
          </button>
          <button
            onClick={() => onSpreadTeamChange('home')}
            className={cn(
              'flex-1 py-1 px-2 rounded text-[11px] font-bold transition-all',
              !isAway
                ? 'bg-gold/20 border border-gold text-gold'
                : 'bg-surface-3 border border-transparent text-warm-gray hover:border-surface-4',
            )}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              <CountryFlag name={game.homeTeam} size="sm" />
              {game.homeTeam}
            </span>
          </button>
        </div>
      )}

      {hasPurchased ? (
        <div className="flex items-center gap-2 text-xs text-warm-gray">
          <span className="flex items-center gap-1">
            <Image src={asset('assets/icons/coins.webp')} alt="" width={12} height={12} />
            {purchasedProp!.costCoins}
          </span>
          <span>{purchasedProp!.bonusMultiplier}x</span>
          <span className="text-warm-gray/60">·</span>
          <span className="text-warm-gray/60">{purchasedProp!.description}</span>
        </div>
      ) : currentProp && (
        <div className="space-y-1.5">
          {/* Current line display */}
          <div className="flex items-center justify-between">
            <div>
              {spreadTeamName && (
                <span className="text-[11px] text-warm-gray/60 mr-1">{spreadTeamName}</span>
              )}
              <span className="text-base font-bold text-cream">
                {isTotal
                  ? `O/U ${currentProp.line ?? '—'}`
                  : displayCurrentLine(currentProp.line)}
              </span>
              <span className="text-[11px] text-warm-gray/60 ml-1.5">{currentProp.bonusMultiplier}x</span>
            </div>
            {confirmPropId === currentProp.id ? (
              <button
                disabled={disabled}
                onClick={() => handleBuyClick(currentProp.id)}
                className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-cream/90 text-[#3a2a1a] hover:bg-cream transition-colors"
              >
                Confirm · {currentProp.costCoins}c
              </button>
            ) : (
              <Button
                variant="coins"
                size="sm"
                disabled={disabled}
                onClick={() => handleBuyClick(currentProp.id)}
              >
                <span className="flex items-center gap-1">
                  Buy
                  <Image src={asset('assets/icons/coins.webp')} alt="" width={12} height={12} />
                  {currentProp.costCoins}
                </span>
              </Button>
            )}
          </div>

          {/* Slider */}
          {!isSingleLine && (
            <div className="space-y-0.5">
              <input
                type="range"
                min={0}
                max={displayProps.length - 1}
                value={Math.min(effectiveIndex, displayProps.length - 1)}
                onChange={(e) => onSlide(category, Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                  bg-gradient-to-r from-verdigris/40 via-gold/40 to-oxblood/40
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-lg
                  [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-cream
                  [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                  [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gold [&::-moz-range-thumb]:shadow-lg
                  [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-cream [&::-moz-range-thumb]:border-0"
              />
              <div className="flex justify-between text-[9px] text-warm-gray/50">
                {isTotal ? (
                  <>
                    <span>O/U {displayProps[0].line}</span>
                    <span>O/U {displayProps[displayProps.length - 1].line}</span>
                  </>
                ) : (
                  <>
                    <span>{displayCurrentLine(displayProps[0].line)}</span>
                    <span>{displayCurrentLine(displayProps[displayProps.length - 1].line)}</span>
                  </>
                )}
              </div>
            </div>
          )}

          <p className="text-[10px] text-warm-gray/60 leading-tight">{currentProp.description}</p>
        </div>
      )}
    </div>
  );
}

/** Props grouped under a game — slider layout per category */
function GamePropGroup({ game, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  game: GameData;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  // Track slider position per category (index into sorted lines)
  const [sliderPositions, setSliderPositions] = useState<Record<string, number>>({});
  // Track which team is selected for spreads — reset slider on switch
  const [spreadTeam, setSpreadTeam] = useState<'home' | 'away'>('home');
  const handleSpreadTeamChange = (team: 'home' | 'away') => {
    setSpreadTeam(team);
    setSliderPositions((prev) => {
      const next = { ...prev };
      delete next['SPREAD']; // Reset so component picks primary for new team
      return next;
    });
  };

  // Group by category (merge split total categories into unified groups)
  const byCategory = new Map<string, PropData[]>();
  for (const prop of props) {
    let cat = prop.category || 'OTHER';
    if (cat === 'TOTAL_OVER' || cat === 'TOTAL_UNDER') cat = 'TOTAL';
    if (cat === 'TEAM_TOTAL_OVER' || cat === 'TEAM_TOTAL_UNDER') cat = 'TEAM_TOTAL';
    const arr = byCategory.get(cat) || [];
    arr.push(prop);
    byCategory.set(cat, arr);
  }

  const categoryOrder = [
    'SPREAD', 'TOTAL', 'TEAM_TOTAL', 'MONEYLINE',
    'PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS',
    'PLAYER_POINTS_REBOUNDS', 'PLAYER_POINTS_ASSISTS', 'PLAYER_REBOUNDS_ASSISTS',
    'OTHER',
  ];
  const sortedCategories = [...byCategory.entries()].sort(
    (a, b) => {
      const ai = categoryOrder.indexOf(a[0]);
      const bi = categoryOrder.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    }
  );

  const purchasedInGame = props.filter((p) => purchasedPropIds.has(p.id)).length;
  const categoryCount = sortedCategories.length;

  // Spread/Moneyline mutual exclusion
  const purchasedCategories = new Set(
    props.filter((p) => purchasedPropIds.has(p.id)).map((p) => p.category)
  );
  const hasSpread = purchasedCategories.has('SPREAD');
  const hasMoneyline = purchasedCategories.has('MONEYLINE');
  const conflictedCategories = new Set<string>();
  if (hasSpread) conflictedCategories.add('MONEYLINE');
  if (hasMoneyline) conflictedCategories.add('SPREAD');

  const handleSlide = (category: string, index: number) => {
    setSliderPositions((prev) => ({ ...prev, [category]: index }));
  };

  const getSliderIndex = (category: string) => {
    return sliderPositions[category] ?? -1; // -1 = let component pick default
  };

  return (
    <div className="rounded-xl border border-surface-3 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-surface-2 hover:bg-surface-3/50 transition-colors"
      >
        <div className="text-left">
          <p className="text-cream text-sm font-semibold flex items-center gap-1.5">
            <CountryFlag name={game.awayTeam} size="md" />
            {game.awayTeam} @ {game.homeTeam}
            <CountryFlag name={game.homeTeam} size="md" />
          </p>
          <p className="text-warm-gray text-xs mt-0.5">{categoryCount} categories</p>
        </div>
        <div className="flex items-center gap-2">
          {purchasedInGame > 0 && (
            <span className="text-xs text-gold font-medium">{purchasedInGame} purchased</span>
          )}
          <span className={cn('text-warm-gray text-sm transition-transform', expanded && 'rotate-180')}>▾</span>
        </div>
      </button>

      {expanded && (
        <div className="p-3 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sortedCategories.map(([category, catProps]) => (
              <CategorySliderGroup
                key={category}
                category={category}
                props={catProps}
                myProps={myProps}
                purchasedPropIds={purchasedPropIds}
                sliderIndex={getSliderIndex(category)}
                onSlide={handleSlide}
                onPurchase={onPurchase}
                disabled={disabled}
                game={game}
                spreadTeam={category === 'SPREAD' ? spreadTeam : undefined}
                onSpreadTeamChange={category === 'SPREAD' ? handleSpreadTeamChange : undefined}
                conflicted={conflictedCategories.has(category)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Extract player name from prop description like "Lionel Messi — Goals O/U 1.5" */
function extractPlayerName(description: string): string {
  const dashIdx = description.indexOf(' — ');
  if (dashIdx > 0) return description.substring(0, dashIdx);
  return 'Unknown';
}

/** Single player's collapsible card with their prop categories */
function PlayerPropCard({ playerName, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  playerName: string;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [sliderPositions, setSliderPositions] = useState<Record<string, number>>({});

  const purchasedCount = props.filter((p) => purchasedPropIds.has(p.id)).length;

  // Group by category
  const byCategory = new Map<string, PropData[]>();
  for (const prop of props) {
    const cat = prop.category || 'OTHER';
    const arr = byCategory.get(cat) || [];
    arr.push(prop);
    byCategory.set(cat, arr);
  }

  const categoryOrder = [
    'PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS',
    'PLAYER_POINTS_REBOUNDS', 'PLAYER_POINTS_ASSISTS', 'PLAYER_REBOUNDS_ASSISTS',
    'OTHER',
  ];
  const sortedCategories = [...byCategory.entries()].sort(
    (a, b) => {
      const ai = categoryOrder.indexOf(a[0]);
      const bi = categoryOrder.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    }
  );

  const handleSlide = (category: string, index: number) => {
    setSliderPositions((prev) => ({ ...prev, [category]: index }));
  };

  const getSliderIndex = (category: string) => {
    return sliderPositions[category] ?? -1; // -1 = let component pick default
  };

  // Format as "Last, First"
  const parts = playerName.split(' ');
  const displayName = parts.length >= 2
    ? `${parts.slice(-1)[0]}, ${parts.slice(0, -1).join(' ')}`
    : playerName;

  return (
    <div className="rounded-lg border border-surface-3 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 bg-surface-2/80 hover:bg-surface-3/50 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <ChevronDown className={cn(
            'w-3 h-3 text-cream/40 transition-transform',
            !expanded && '-rotate-90',
          )} />
          <span className="text-cream text-xs font-medium">{displayName}</span>
          <span className="text-warm-gray/50 text-[10px]">{sortedCategories.length} cats</span>
        </div>
        {purchasedCount > 0 && (
          <span className="text-[10px] text-gold font-medium">{purchasedCount} purchased</span>
        )}
      </button>

      {expanded && (
        <div className="p-2 pt-1.5 grid grid-cols-2 gap-1.5">
          {sortedCategories.map(([category, catProps]) => (
            <CategorySliderGroup
              key={category}
              category={category}
              props={catProps}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              sliderIndex={getSliderIndex(category)}
              onSlide={handleSlide}
              onPurchase={onPurchase}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Player props grouped under a game — game header, then collapsible player cards */
function GamePlayerPropGroup({ game, props, myProps, purchasedPropIds, onPurchase, disabled }: {
  game: GameData;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(true);

  // Group props by player name (from description)
  const byPlayer = new Map<string, PropData[]>();
  for (const prop of props) {
    const name = extractPlayerName(prop.description);
    const arr = byPlayer.get(name) || [];
    arr.push(prop);
    byPlayer.set(name, arr);
  }

  // Sort players: highest points line first, then alphabetical by last name
  const sortedPlayers = [...byPlayer.entries()].sort((a, b) => {
    const aPointsProp = a[1].find((p) => p.category === 'PLAYER_POINTS');
    const bPointsProp = b[1].find((p) => p.category === 'PLAYER_POINTS');
    const aLine = aPointsProp?.line ?? 0;
    const bLine = bPointsProp?.line ?? 0;
    if (bLine !== aLine) return bLine - aLine;
    const aLast = a[0].split(' ').slice(-1)[0] || a[0];
    const bLast = b[0].split(' ').slice(-1)[0] || b[0];
    return aLast.localeCompare(bLast);
  });

  const purchasedInGame = props.filter((p) => purchasedPropIds.has(p.id)).length;

  return (
    <div className="rounded-xl border border-surface-3 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-surface-2 hover:bg-surface-3/50 transition-colors"
      >
        <div className="text-left">
          <p className="text-cream text-sm font-semibold flex items-center gap-1.5">
            <CountryFlag name={game.awayTeam} size="md" />
            {game.awayTeam} @ {game.homeTeam}
            <CountryFlag name={game.homeTeam} size="md" />
          </p>
          <p className="text-warm-gray text-xs mt-0.5">{sortedPlayers.length} players</p>
        </div>
        <div className="flex items-center gap-2">
          {purchasedInGame > 0 && (
            <span className="text-xs text-gold font-medium">{purchasedInGame} purchased</span>
          )}
          <span className={cn('text-warm-gray text-sm transition-transform', expanded && 'rotate-180')}>▾</span>
        </div>
      </button>

      {expanded && (
        <div className="p-3 pt-2 space-y-2">
          {sortedPlayers.map(([playerName, playerProps]) => (
            <PlayerPropCard
              key={playerName}
              playerName={playerName}
              props={playerProps}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              onPurchase={onPurchase}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Standalone prop card for props without a game reference */
function StandalonePropCard({ prop, purchased, onPurchase, disabled, purchasingPropId }: {
  prop: PropData;
  purchased: MyPropData | null;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const isResolved = purchased?.result === 'HIT' || purchased?.result === 'MISS';
  const purchasePending = purchasingPropId !== null;

  // Condensed for non-purchased when locked
  if (disabled && !purchased) {
    return (
      <div className="rounded-lg border border-surface-3/50 bg-surface-2/30 px-2.5 py-1.5 flex items-center justify-between">
        <span className="text-[10px] text-warm-gray/40 truncate mr-2">{prop.description}</span>
        <span className="text-[10px] text-warm-gray/30 whitespace-nowrap">{prop.bonusMultiplier}x</span>
      </div>
    );
  }

  return (
    <div className={cn(
      'p-4 rounded-xl border',
      purchased ? 'bg-surface-2/50 border-surface-3' : 'bg-surface-2 border-surface-3 hover:border-gold/30',
    )}>
      <p className="text-cream text-sm font-medium mb-2">{prop.description}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-warm-gray">
          <span className="flex items-center gap-1">
            <Image src={asset('assets/icons/coins.webp')} alt="" width={14} height={14} />
            {prop.costCoins}
          </span>
          <span>{prop.bonusMultiplier}x bonus</span>
        </div>

        {purchased ? (
          <span className={cn(
            'px-3 py-1 rounded-lg text-xs font-bold',
            isResolved
              ? purchased.result === 'HIT'
                ? 'bg-verdigris/20 text-verdigris'
                : 'bg-oxblood/20 text-oxblood'
              : 'bg-gold/10 text-gold'
          )}>
            {isResolved ? (purchased.result === 'HIT' ? `HIT +${fmtPts(purchased.bonusPoints)}` : 'MISS') : 'Purchased'}
          </span>
        ) : (
          <Button
            variant="coins"
            size="sm"
            disabled={disabled || purchasePending}
            onClick={() => onPurchase(prop.id)}
          >
            {purchasingPropId === prop.id ? 'Buying...' : 'Buy'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MANUAL PREDICTIONS (Yes/No or Buy-Only, admin-authored)
// ============================================================================

/**
 * Compact inline row for a manual prediction. Used both inside UnifiedGameCard
 * (per-match predictions stack under Spread/Total) and in the round-wide section.
 * Keeps to a single horizontal layout: text on the left, controls on the right.
 */
function InlineManualPropRow({
  prop,
  gameSettled,
  purchased,
  onPurchase,
  disabled,
  purchasingPropId,
}: {
  prop: PropData;
  gameSettled: boolean;
  purchased: MyPropData | null;
  onPurchase: (propId: string, userSelection?: 'YES' | 'NO') => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const isYesNo = prop.pickMode === 'YES_NO';
  const isResolved = purchased?.result === 'HIT' || purchased?.result === 'MISS';
  const userPicked = purchased?.userSelection ?? null;
  const purchasePending = purchasingPropId !== null;
  const isPurchasingCurrent = purchasingPropId === prop.id;
  const buttonsDisabled = disabled || purchasePending || !!purchased || gameSettled;

  // 2-step click confirmation: first click stages the side, second click commits.
  // Auto-cancels after 3s so a stale "Confirm?" can't lock the user mid-thought.
  // Mirrors the existing odds-based prop pattern (SpreadSection, TotalSection).
  const [pendingSide, setPendingSide] = useState<'YES' | 'NO' | 'BUY' | null>(null);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPending = useCallback(() => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = null;
    setPendingSide(null);
  }, []);
  useEffect(() => () => clearPending(), [clearPending]);
  // Cancel pending state if the prop becomes purchased/locked from elsewhere.
  useEffect(() => {
    if (purchased || gameSettled || disabled) clearPending();
  }, [purchased, gameSettled, disabled, clearPending]);

  const handleClick = (side: 'YES' | 'NO' | 'BUY') => {
    if (pendingSide === side) {
      clearPending();
      onPurchase(prop.id, side === 'BUY' ? undefined : side);
      return;
    }
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setPendingSide(side);
    confirmTimerRef.current = setTimeout(() => setPendingSide(null), 3000);
  };

  return (
    <div className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-surface-3/20 @max-[14rem]:flex-col @max-[14rem]:items-stretch">
      <div className="flex items-start gap-2 flex-1 min-w-0">
      <CountryFlag name={prop.subjectTeam} size="md" className="mt-0.5 self-start" />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-cream/90 leading-tight line-clamp-2">{prop.description}</p>
        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-warm-gray/50">
          <span>{prop.bonusMultiplier}x</span>
          <span>·</span>
          <span>{prop.costCoins}c</span>
          {pendingSide && !purchased && (
            <>
              <span>·</span>
              <span className="text-gold font-bold">Tap again to confirm</span>
            </>
          )}
          {isPurchasingCurrent && (
            <>
              <span>·</span>
              <span className="text-gold font-bold">Buying...</span>
            </>
          )}
          {isResolved && (
            <>
              <span>·</span>
              <span
                className={cn(
                  'font-bold',
                  purchased!.result === 'HIT' ? 'text-verdigris' : 'text-oxblood'
                )}
              >
                {purchased!.result === 'HIT' ? `HIT +${fmtPts(purchased!.bonusPoints)}` : 'MISS'}
              </span>
            </>
          )}
        </div>
      </div>
      </div>

      {isYesNo ? (
        <div className="flex items-center gap-1 shrink-0 @max-[14rem]:w-full @max-[14rem]:justify-end @max-[14rem]:mt-1">
          <button
            type="button"
            aria-pressed={userPicked === 'YES'}
            aria-label={
              userPicked === 'YES'
                ? 'Your pick: Yes'
                : pendingSide === 'YES'
                  ? 'Tap again to confirm Yes'
                  : 'Pick Yes'
            }
            onClick={() => handleClick('YES')}
            disabled={buttonsDisabled}
            className={cn(
              'h-7 w-12 rounded text-[10px] font-bold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
              userPicked === 'YES'
                ? isResolved
                  ? purchased!.result === 'HIT'
                    ? 'bg-verdigris/30 text-verdigris border border-verdigris'
                    : 'bg-oxblood/30 text-oxblood border border-oxblood'
                  : 'bg-gold/30 text-gold border border-gold'
                : pendingSide === 'YES'
                  ? 'bg-gold/40 text-gold border border-gold ring-1 ring-gold animate-pulse'
                  : purchased || gameSettled
                    ? 'bg-surface-3/30 text-warm-gray/30 border border-surface-3 cursor-not-allowed'
                    : 'bg-verdigris/10 text-verdigris hover:bg-verdigris/20 border border-verdigris/30 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {pendingSide === 'YES' ? '✓ YES' : 'YES'}
          </button>
          <button
            type="button"
            aria-pressed={userPicked === 'NO'}
            aria-label={
              userPicked === 'NO'
                ? 'Your pick: No'
                : pendingSide === 'NO'
                  ? 'Tap again to confirm No'
                  : 'Pick No'
            }
            onClick={() => handleClick('NO')}
            disabled={buttonsDisabled}
            className={cn(
              'h-7 w-12 rounded text-[10px] font-bold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
              userPicked === 'NO'
                ? isResolved
                  ? purchased!.result === 'HIT'
                    ? 'bg-verdigris/30 text-verdigris border border-verdigris'
                    : 'bg-oxblood/30 text-oxblood border border-oxblood'
                  : 'bg-gold/30 text-gold border border-gold'
                : pendingSide === 'NO'
                  ? 'bg-gold/40 text-gold border border-gold ring-1 ring-gold animate-pulse'
                  : purchased || gameSettled
                    ? 'bg-surface-3/30 text-warm-gray/30 border border-surface-3 cursor-not-allowed'
                    : 'bg-oxblood/10 text-oxblood hover:bg-oxblood/20 border border-oxblood/30 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {pendingSide === 'NO' ? '✓ NO' : 'NO'}
          </button>
        </div>
      ) : (
        <div className="shrink-0 @max-[14rem]:w-full @max-[14rem]:flex @max-[14rem]:justify-end @max-[14rem]:mt-1">
          {purchased ? (
            <span className="px-2 py-1 rounded text-[10px] font-bold bg-gold/10 text-gold">Bought</span>
          ) : (
            <button
              type="button"
              onClick={() => handleClick('BUY')}
              disabled={buttonsDisabled}
              className={cn(
                'h-7 px-3 rounded text-[10px] font-bold border disabled:opacity-50 disabled:cursor-not-allowed',
                pendingSide === 'BUY'
                  ? 'bg-gold/40 text-gold border-gold ring-1 ring-gold animate-pulse'
                  : 'bg-gold/10 text-gold hover:bg-gold/20 border-gold/30'
              )}
            >
              {isPurchasingCurrent ? 'Buying...' : pendingSide === 'BUY' ? '✓ Confirm' : 'Buy'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

// Whether a single game's picks are locked right now. SCORING/COMPLETED hard-lock
// the whole round in every mode; LOCKED hard-locks only in WHOLE_UNDERCARD. Otherwise
// a game locks at its own effective lockTime (gameTime − offset in PER_GAME).
function isGameLockedAt(
  game: GameData,
  undercard: { status: string; lockMode: string },
  nowMs: number,
): boolean {
  const hardLocked =
    undercard.status === 'SCORING' ||
    undercard.status === 'COMPLETED' ||
    (undercard.lockMode === 'WHOLE_UNDERCARD' && undercard.status === 'LOCKED');
  return hardLocked || new Date(game.lockTime).getTime() <= nowMs;
}

export default function UndercardPage() {
  const router = useRouter();
  const { accessToken } = useAuthStore();
  const [undercard, setUndercard] = useState<UndercardDetail | null>(null);
  const [draftPicks, setDraftPicks] = useState<Map<string, PickSelection>>(new Map());
  const [savedPicks, setSavedPicks] = useState<GamePickData[]>([]);
  const [myProps, setMyProps] = useState<MyPropData[]>([]);
  const [hasEntered, setHasEntered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [entering, setEntering] = useState(false);
  const [purchasingPropId, setPurchasingPropId] = useState<string | null>(null);
  // Synchronous guard so only one prop purchase can start before React
  // re-renders the controls into their pending state.
  const inFlightProps = useRef<Set<string>>(new Set());
  const [maxProps, setMaxProps] = useState(5);
  const [purchasedCount, setPurchasedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Migrated from Next.js: original awaited a Promise `params` prop.
  // React Router exposes both segments synchronously.
  const { id: eventIdParam, undercardId: undercardIdParam } = useParams<{
    id: string;
    undercardId: string;
  }>();
  const [eventId, setEventId] = useState(eventIdParam ?? '');
  const [undercardId, setUndercardId] = useState(undercardIdParam ?? '');
  const [accessCode, setAccessCode] = useState('');
  const [parentAccessType, setParentAccessType] = useState<string>('OPEN');
  const [parentEntryFee, setParentEntryFee] = useState(0);
  // Event-level join eligibility (any round still has an open game). Entry is
  // event-scoped, so Enter must not be gated on this round's lock state.
  const [eventJoinable, setEventJoinable] = useState(false);
  const [siblingRounds, setSiblingRounds] = useState<RoundStep[]>([]);
  // undercardId → number of matches the user has saved picks for (for the round
  // stepper's per-round pick-progress dashes). The currently viewed round is
  // overlaid with the live draft count at render time.
  const [picksByRound, setPicksByRound] = useState<Record<string, number>>({});
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [myEntryData, setMyEntryData] = useState<{
    undercardEntryId: string;
    pickScore: number;
    propBonusScore: number;
    totalScore: number;
    accuracyModifier: number;
  } | null>(null);

  useEffect(() => {
    if (eventIdParam) setEventId(eventIdParam);
    if (undercardIdParam) setUndercardId(undercardIdParam);
  }, [eventIdParam, undercardIdParam]);

  // Live tick — re-render every 1s so per-game lock state (computed from nowMs)
  // flips cards to Locked at their lock time without a manual refresh, in step
  // with the 1s CountdownTimer (a coarser tick would let a card stay pickable for
  // up to the interval after the countdown already shows locked).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // When the live tick locks a game, snap its draft pick back to the saved truth so a
  // locked card never shows a selection that was never persisted (keeps the action
  // bar's unsaved state honest). Mirrors the skipped-games reconcile after a save.
  useEffect(() => {
    if (!undercard) return;
    setDraftPicks((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const game of undercard.games) {
        if (!isGameLockedAt(game, undercard, nowMs)) continue;
        const saved = savedPicks.find((p) => p.gameId === game.id)?.pickedWinner;
        const cur = next.get(game.id);
        if (saved === undefined) {
          if (cur !== undefined) { next.delete(game.id); changed = true; }
        } else if (cur !== saved) {
          next.set(game.id, saved);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [nowMs, undercard, savedPicks]);

  const fetchData = useCallback(async () => {
    if (!eventId || !undercardId) return;
    try {
      setLoading(true);
      // Reset per-undercard state up front so stale picks/entry from the
      // previously viewed undercard can't drive notification scheduling for
      // this one (e.g. skipping a prop_lock because the prior undercard was
      // already picked).
      setSavedPicks([]);
      setHasEntered(false);
      const [ucRes, eventRes, lbRes] = await Promise.all([
        fetch(`${API_BASE}/api/main-events/${eventId}/undercards/${undercardId}`),
        fetch(`${API_BASE}/api/main-events/${eventId}`),
        fetch(`${API_BASE}/api/main-events/${eventId}/leaderboard?limit=10`),
      ]);
      if (lbRes.ok) setLeaderboard(await lbRes.json());
      if (ucRes.ok) setUndercard(await ucRes.json());
      else setError('Undercard not found');
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setParentAccessType(eventData.accessType ?? 'OPEN');
        setParentEntryFee(eventData.entryFee ?? 0);
        setEventJoinable(!!eventData.joinable);
        if (eventData.undercards) {
          setSiblingRounds(
            eventData.undercards.map((uc: UndercardSummary) => ({
              id: uc.id,
              title: uc.title,
              status: uc.status,
              round: uc.round,
              gameCount: uc.gameCount,
            }))
          );
        }
      } else {
        // Event fetch failed — clear stale joinability so Enter/Sign-in can't linger
        // from a previously viewed event.
        setEventJoinable(false);
      }

      // Fetch prop limits (works for both auth and unauth)
      const propsInfoRes = isLoggedIn()
        ? await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/props`)
        : await fetch(`${API_BASE}/api/main-events/${eventId}/undercards/${undercardId}/props`);
      if (propsInfoRes.ok) {
        const propsData = await propsInfoRes.json();
        setMaxProps(propsData.maxProps ?? 5);
        setPurchasedCount(propsData.purchasedCount ?? 0);
      }

      if (isLoggedIn()) {
        // Check if user has entered the event + extract undercard scores
        const entryRes = await authFetch(`/api/main-events/${eventId}/my-entry`);
        setHasEntered(entryRes.ok);
        if (entryRes.ok) {
          const entryData = await entryRes.json();
          // Per-round saved-pick counts feed the round stepper's progress dashes.
          const counts: Record<string, number> = {};
          for (const ue of entryData.undercardEntries ?? []) {
            counts[ue.undercardId] = ue.picksSubmitted ?? 0;
          }
          setPicksByRound(counts);
          const ucEntry = entryData.undercardEntries?.find(
            (ue: { undercardId: string }) => ue.undercardId === undercardId
          );
          if (ucEntry) {
            setMyEntryData({
              undercardEntryId: ucEntry.id,
              pickScore: ucEntry.pickScore,
              propBonusScore: ucEntry.propBonusScore,
              totalScore: ucEntry.totalScore,
              accuracyModifier: ucEntry.accuracyModifier ?? 1.0,
            });
          }
        }

        const [picksRes, propsRes] = await Promise.all([
          authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/my-picks`),
          authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/my-props`),
        ]);
        if (picksRes.ok) {
          const picks: GamePickData[] = await picksRes.json();
          setSavedPicks(picks);
          const map = new Map<string, PickSelection>();
          picks.forEach((p) => map.set(p.gameId, p.pickedWinner));
          setDraftPicks(map);
        }
        if (propsRes.ok) setMyProps(await propsRes.json());
      }
    } catch {
      setError('Failed to load undercard');
    } finally {
      setLoading(false);
    }
  }, [eventId, undercardId, accessToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    // Wait until this undercard's data has finished loading — otherwise the
    // effect runs with picks still in flight (or stale from another undercard)
    // and would schedule/skip prop_lock incorrectly.
    if (!undercard || loading) return;
    const sdk = typeof window !== 'undefined' ? window.JestSDK : null;
    // Saved picks > 0 → the user has progressed past the "needs reminder"
    // state, so the helper will skip prop_lock and clear any prior dedupe.
    const hasMadePicks = savedPicks.length > 0;
    void scheduleUndercardNotifications(sdk, undercard, {
      hasEntry: hasEntered,
      hasMadePicks,
    });
  }, [undercard, hasEntered, savedPicks, loading]);

  const handlePick = (gameId: string, winner: PickSelection) => {
    setDraftPicks((prev) => {
      const next = new Map(prev);
      if (next.get(gameId) === winner) {
        next.delete(gameId);
      } else {
        next.set(gameId, winner);
      }
      return next;
    });
  };

  const handleClearPicks = () => {
    // Reset to saved picks (or empty if none saved)
    const map = new Map<string, PickSelection>();
    savedPicks.forEach((p) => map.set(p.gameId, p.pickedWinner));
    setDraftPicks(map);
  };

  const handleEnterAndSave = async () => {
    if (!isLoggedIn()) {
      router.push('/login');
      return;
    }
    try {
      setEntering(true);
      setError(null);

      // Enter the event
      const enterBody = parentAccessType === 'CODE_REQUIRED' ? { accessCode } : undefined;
      const enterRes = await authFetch(`/api/main-events/${eventId}/enter`, {
        method: 'POST',
        ...(enterBody ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(enterBody),
        } : {}),
      });
      if (!enterRes.ok) {
        const data = await enterRes.json().catch(() => ({} as { error?: string; code?: string }));
        // event closed (last game locked) while the page sat open on a stale
        // joinable snapshot — hide Enter so it can't be retried into the same error.
        if (data.code === 'EVENT_NOT_OPEN') setEventJoinable(false);
        throw new Error(data.error || 'Failed to enter event');
      }
      setHasEntered(true);

      // If user has picks selected, save them too
      if (draftPicks.size > 0) {
        const picks = Array.from(draftPicks.entries()).map(([gameId, pickedWinner]) => ({
          gameId,
          pickedWinner,
        }));
        const res = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/picks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ picks }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to submit picks');
        }
        const result = await res.json().catch(() => ({}));
        const skipped: string[] = result.skippedGameIds ?? result.data?.skippedGameIds ?? [];
        const savedCount: number = result.picksCount ?? result.data?.picksCount ?? 0;
        // Refresh saved picks
        const picksRes = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/my-picks`);
        const fresh: GamePickData[] | null = picksRes.ok ? await picksRes.json() : null;
        if (fresh) setSavedPicks(fresh);
        // Reconcile any games that locked between pick and save.
        if (skipped.length > 0 && fresh) {
          setDraftPicks((prev) => {
            const next = new Map(prev);
            for (const id of skipped) {
              const s = fresh.find((p) => p.gameId === id);
              if (s) next.set(id, s.pickedWinner);
              else next.delete(id);
            }
            return next;
          });
        }
        if (savedCount > 0) {
          void markFirstMilestoneOnce(typeof window !== 'undefined' ? window.JestSDK : null);
        }
        setSuccessMsg(
          skipped.length > 0
            ? savedCount > 0
              ? `Entered — saved ${savedCount}, ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
              : `Entered — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
            : 'Entered event and saved picks!'
        );
      } else {
        setSuccessMsg('Entered event!');
      }
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enter');
      setTimeout(() => setError(null), 4000);
    } finally {
      setEntering(false);
    }
  };

  const handleSubmitPicks = async () => {
    if (draftPicks.size === 0) return;
    if (!isLoggedIn()) {
      router.push('/login');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const picks = Array.from(draftPicks.entries()).map(([gameId, pickedWinner]) => ({
        gameId,
        pickedWinner,
      }));
      const res = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ picks }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to submit picks');
      }
      const result = await res.json().catch(() => ({}));
      const skipped: string[] = result.skippedGameIds ?? result.data?.skippedGameIds ?? [];
      const savedCount: number = result.picksCount ?? result.data?.picksCount ?? 0;
      // Refresh saved picks from the server.
      const picksRes = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/my-picks`);
      const fresh: GamePickData[] | null = picksRes.ok ? await picksRes.json() : null;
      if (fresh) setSavedPicks(fresh);
      // Reconcile games the server skipped (locked between pick and save) back to the
      // real saved state so a locked card stops showing a pick that never persisted.
      if (skipped.length > 0 && fresh) {
        setDraftPicks((prev) => {
          const next = new Map(prev);
          for (const id of skipped) {
            const s = fresh.find((p) => p.gameId === id);
            if (s) next.set(id, s.pickedWinner);
            else next.delete(id);
          }
          return next;
        });
      }
      if (savedCount > 0) {
        void markFirstMilestoneOnce(typeof window !== 'undefined' ? window.JestSDK : null);
      }
      setSuccessMsg(
        skipped.length > 0
          ? savedCount > 0
            ? `Saved ${savedCount} — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
            : `Too late — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
          : 'Picks saved!'
      );
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
      setTimeout(() => setError(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchaseProp = async (propId: string, userSelection?: 'YES' | 'NO') => {
    if (!isLoggedIn()) {
      router.push('/login');
      return;
    }
    if (!hasEntered) {
      setError('You must enter the event before purchasing props');
      setTimeout(() => setError(null), 4000);
      return;
    }
    // Block double-clicks and purchases of a second prop while one is pending.
    if (inFlightProps.current.size > 0) return;
    inFlightProps.current.add(propId);
    try {
      setPurchasingPropId(propId);
      setError(null);
      const body: Record<string, unknown> = { propId };
      if (userSelection) body.userSelection = userSelection;
      const res = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/props/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to purchase prop');
      }
      // Refetch the balance so the header coins reflect the spend.
      void refreshBalance();
      // Refresh from server before optimistic UI update so counter and toast align.
      const propsRes = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}/my-props`);
      if (propsRes.ok) {
        const fresh: MyPropData[] = await propsRes.json();
        setMyProps(fresh);
        setPurchasedCount(fresh.length);
      } else {
        // Refetch failed — fall back to optimistic increment so UI reflects the success.
        setPurchasedCount((prev) => prev + 1);
      }
      setSuccessMsg(userSelection ? `Picked ${userSelection}!` : 'Prop purchased!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to purchase');
      setTimeout(() => setError(null), 4000);
    } finally {
      setPurchasingPropId(null);
      inFlightProps.current.delete(propId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!undercard) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-warm-gray">Undercard not found</p>
      </div>
    );
  }

  const isGameLocked = (game: GameData) => isGameLockedAt(game, undercard, nowMs);
  // Terminal statuses lock the round regardless of its games (also covers a
  // zero-game round, where allGamesLocked would be false); LOCKED hard-locks only
  // in WHOLE_UNDERCARD. Mirrors the hardLocked branch of isGameLockedAt.
  const roundStatusLocked =
    undercard.status === 'SCORING' ||
    undercard.status === 'COMPLETED' ||
    (undercard.lockMode === 'WHOLE_UNDERCARD' && undercard.status === 'LOCKED');
  // Page-level gate (action bar / countdown) — true when the round is status-locked
  // or EVERY game is locked. Per-card locking is applied on each UnifiedGameCard below.
  const allGamesLocked = undercard.games.length > 0 && undercard.games.every(isGameLocked);
  const picksDisabled = roundStatusLocked || allGamesLocked;
  // Round-wide / standalone props lock at the undercard's single lockTime, mirroring
  // the server: SCORING/COMPLETED always; LOCKED only in WHOLE_UNDERCARD; else by time.
  const roundWideLocked =
    undercard.status === 'SCORING' ||
    undercard.status === 'COMPLETED' ||
    (undercard.lockMode === 'WHOLE_UNDERCARD' && undercard.status === 'LOCKED') ||
    nowMs >= new Date(undercard.lockTime).getTime();
  // Next upcoming game lock, for the countdown (PER_GAME shows the next game to lock).
  const nextLockTime =
    undercard.lockMode === 'PER_GAME'
      ? (undercard.games
          .map((g) => new Date(g.lockTime))
          .filter((d) => d.getTime() > nowMs)
          .sort((a, b) => a.getTime() - b.getTime())[0] ?? null)
      : new Date(undercard.lockTime);

  const isOpenGame = (gameId: string) => {
    const g = undercard.games.find((x) => x.id === gameId);
    return !!g && !isGameLocked(g);
  };

  // Count how many picks differ from saved (only still-open games can be unsaved).
  const hasUnsavedPicks = Array.from(draftPicks.entries()).some(([gameId, winner]) => {
    if (!isOpenGame(gameId)) return false;
    const saved = savedPicks.find((p) => p.gameId === gameId);
    return !saved || saved.pickedWinner !== winner;
  });

  // Has any draft picks at all (for clear button)
  const hasDraftPicks = draftPicks.size > 0;

  // Computed scores
  const pickPoints = savedPicks.reduce((sum, p) => sum + (p.pointsEarned ?? 0), 0);
  const propBonusPoints = myProps.reduce((sum, p) => sum + (p.bonusPoints ?? 0), 0);
  const correctPicks = savedPicks.filter((p) => p.isCorrect === true).length;
  const settledPicks = savedPicks.filter((p) => p.isCorrect !== null).length;
  const hitProps = myProps.filter((p) => p.result === 'HIT').length;
  const settledMyProps = myProps.filter((p) => p.result === 'HIT' || p.result === 'MISS').length;
  const isCompleted = undercard.status === 'COMPLETED';
  const accuracyMod = myEntryData?.accuracyModifier ?? 1.0;
  const totalScore = isCompleted && myEntryData ? myEntryData.totalScore : pickPoints + propBonusPoints;

  // Round stepper data — overlay each round with its pick-progress count. The
  // round currently being viewed uses the live draft count so dashes fill in as
  // the user picks; other rounds use their saved-pick totals.
  const stepperRounds: RoundStep[] = siblingRounds.map((r) => ({
    ...r,
    picksMade: r.id === undercardId ? draftPicks.size : (picksByRound[r.id] ?? 0),
  }));

  // Floating action bar — Clear / Enter & Save / Sign in to Enter / Save Picks.
  const showClear = !picksDisabled && hasDraftPicks && hasUnsavedPicks;
  // Entry is event-scoped: show Enter whenever the event is still joinable (any
  // round has an open game), even if THIS round is fully locked. The server
  // (enterEvent) enforces the same rule, so there is no client/server drift.
  const showEnter = eventJoinable && !hasEntered && !!accessToken;
  const showSignIn = eventJoinable && !accessToken && hasDraftPicks;
  const showSave = !picksDisabled && hasEntered && hasUnsavedPicks;
  const showActionBar = showClear || showEnter || showSignIn || showSave;

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />
      <main className={cn('max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8', showActionBar && 'pb-40 sm:pb-36')}>
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => router.push(`/events/main/${eventId}`)}
            className="text-sm text-tertiary hover:text-cream mb-4 inline-block"
          >
            ← Back to Event
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-serif font-bold text-cream mb-1 break-words">{undercard.title}</h1>
              <div className="flex items-center gap-3 text-xs sm:text-sm text-warm-gray">
                <span>{undercard.gameCount} games</span>
                <span>{undercard.pointsPerCorrectPick} points/pick</span>
              </div>
            </div>

          </div>
        </div>

        {/* Score Banner */}
        {hasEntered && (
          <ScoreBanner
            pickPoints={pickPoints}
            propBonusPoints={propBonusPoints}
            correctPicks={correctPicks}
            settledPicks={settledPicks}
            totalPicks={undercard.games.length}
            hitProps={hitProps}
            settledMyProps={settledMyProps}
            totalMyProps={myProps.length}
            isCompleted={isCompleted}
            accuracyModifier={accuracyMod}
            pointsPerCorrectPick={undercard.pointsPerCorrectPick}
            totalScore={totalScore}
          />
        )}

        {/* Share My Picks — surfaces the prediction-card image generator.
            Only shown when user has entered AND has picks/props locked in.
            Switches to "Winner Card" variant once the undercard is settled. */}
        {hasEntered && myEntryData && (myProps.length > 0 || savedPicks.length > 0) && (
          <div className="mb-4 flex justify-end">
            <ShareCardButton
              undercardEntryId={myEntryData.undercardEntryId}
              variant={isCompleted ? 'winner' : 'prediction'}
            />
          </div>
        )}

        {/* Countdown Timer — next game to lock (PER_GAME) or the round lockTime */}
        {!picksDisabled && nextLockTime && (
          <div className="mb-6">
            <CountdownTimer lockTime={nextLockTime} />
          </div>
        )}

        {/* Toast notifications — fixed at bottom center */}
        {(successMsg || error) && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <div className={cn(
              'px-5 py-3 rounded-xl border shadow-lg text-sm font-medium max-w-md',
              successMsg
                ? 'bg-surface-2 border-verdigris/30 text-verdigris'
                : 'bg-surface-2 border-oxblood/30 text-oxblood'
            )}>
              {successMsg || error}
            </div>
          </div>
        )}

        {/* Purchased Props Summary Tiles */}
        <PurchasedPropsSummary
          myProps={myProps}
          purchasedCount={purchasedCount}
          maxProps={maxProps}
        />

        {/* Prediction Boosts — Unified Game Cards */}
        {(() => {
          const TEAM_CATEGORIES = new Set(['SPREAD', 'TOTAL', 'TOTAL_OVER', 'TOTAL_UNDER', 'MONEYLINE', 'TEAM_TOTAL_OVER', 'TEAM_TOTAL_UNDER']);
          const UNIFIED_CATEGORIES = new Set(['SPREAD', 'TOTAL', 'TOTAL_OVER', 'TOTAL_UNDER', 'MONEYLINE']);
          const purchasedPropIds = new Set(myProps.map((p) => p.undercardPropId));
          const atLimit = purchasedCount >= maxProps;
          // Round-wide / standalone props lock at the undercard lockTime.
          const propDisabledRoundWide = roundWideLocked || !hasEntered;

          // Manual props (admin-authored, propType !== 'ODDS_BASED') split:
          //   - per-game → embedded inside each UnifiedGameCard
          //   - round-wide (no gameId or gameId not in this undercard) → standalone section
          const manualByGame = new Map<string, PropData[]>();
          const manualRoundWide: PropData[] = [];
          const oddsBased: PropData[] = [];
          const gameIdSet = new Set(undercard.games.map((g) => g.id));
          for (const prop of undercard.props) {
            const isManual = prop.propType && prop.propType !== 'ODDS_BASED';
            if (!isManual) {
              oddsBased.push(prop);
              continue;
            }
            if (prop.gameId && gameIdSet.has(prop.gameId)) {
              const arr = manualByGame.get(prop.gameId) ?? [];
              arr.push(prop);
              manualByGame.set(prop.gameId, arr);
            } else {
              manualRoundWide.push(prop);
            }
          }

          // Build team props map for unified cards (SPREAD, TOTAL, MONEYLINE only)
          const unifiedByGame = new Map<string, PropData[]>();
          const playerByGame = new Map<string, PropData[]>();
          const standalone: PropData[] = [];

          for (const prop of oddsBased) {
            if (!prop.gameId) {
              standalone.push(prop);
              continue;
            }
            const isUnified = prop.category && UNIFIED_CATEGORIES.has(prop.category);
            const isTeam = !prop.category || TEAM_CATEGORIES.has(prop.category);
            if (isUnified) {
              const arr = unifiedByGame.get(prop.gameId) || [];
              arr.push(prop);
              unifiedByGame.set(prop.gameId, arr);
            } else if (!isTeam) {
              // Player prop
              const arr = playerByGame.get(prop.gameId) || [];
              arr.push(prop);
              playerByGame.set(prop.gameId, arr);
            } else {
              // Non-unified team props (TEAM_TOTAL etc.) — keep in unified card area for now
              const arr = unifiedByGame.get(prop.gameId) || [];
              arr.push(prop);
              unifiedByGame.set(prop.gameId, arr);
            }
          }

          const gameMap = new Map(undercard.games.map((g) => [g.id, g]));
          const hasPlayerProps = playerByGame.size > 0;

          // Count total prediction boosts (games + team props)
          const teamPropCount = [...unifiedByGame.values()].reduce((sum, arr) => sum + arr.length, 0);
          const boostCount = undercard.games.length + teamPropCount;

          const REGION_COLORS: Record<string, string> = {
            East: 'text-blue-400',
            West: 'text-red-400',
            South: 'text-green-400',
            Midwest: 'text-amber-400',
          };

          // Check if games have groups (regions)
          const hasGroups = undercard.games.some((g) => g.group);

          // Helper to render a UnifiedGameCard for a game
          const renderUnifiedCard = (game: GameData) => {
            const gameProps = unifiedByGame.get(game.id) || [];
            const gamePProps = playerByGame.get(game.id) || [];
            const gameManual = manualByGame.get(game.id) || [];
            // Per-card lock — each game locks at its own time in PER_GAME mode.
            const gameLocked = isGameLocked(game);
            const propDisabledForCard = gameLocked || !hasEntered;
            // At-limit only blocks new purchases; if user already bought any prop on this card,
            // the locked-state UI still needs to render. Apply the same logic for manual props.
            const hasOwnedHere =
              gameProps.some((p) => purchasedPropIds.has(p.id)) ||
              gameManual.some((p) => purchasedPropIds.has(p.id));
            return (
              <div key={game.id} className="snap-start shrink-0 w-[86%] max-w-sm">
                <UnifiedGameCard
                  game={game}
                  pick={draftPicks.get(game.id) ?? null}
                  onPick={handlePick}
                  picksDisabled={gameLocked}
                  pointsPerCorrectPick={undercard.pointsPerCorrectPick}
                  props={gameProps}
                  playerProps={gamePProps}
                  manualProps={gameManual}
                  myProps={myProps}
                  purchasedPropIds={purchasedPropIds}
                  onPurchase={handlePurchaseProp}
                  propDisabled={propDisabledForCard || (atLimit && !hasOwnedHere)}
                  purchasingPropId={purchasingPropId}
                />
              </div>
            );
          };

          return (
            <div className="space-y-8">
              {/* Header */}
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase">
                    Prediction Boosts
                    <span className="text-warm-gray/50 ml-2 normal-case tracking-normal font-normal">({boostCount})</span>
                  </span>
                  {undercard.props.length > 0 && (
                    <span className="text-xs text-warm-gray">
                      {purchasedCount}/{maxProps} props selected
                    </span>
                  )}
                </div>
                <p className="text-warm-gray text-sm mt-1">
                  Pick game winners and boost your score with spread and total props.
                </p>
              </div>

              {/* Round stepper — switch rounds + per-round pick progress, sitting
                  right above the match carousel */}
              {stepperRounds.length > 1 && (
                <RoundStepper
                  rounds={stepperRounds}
                  currentRoundId={undercardId}
                  eventId={eventId}
                  onNavigate={(id) =>
                    router.push(`/events/main/${eventId}/undercards/${id}`)
                  }
                  sticky={false}
                />
              )}

              {/* Unified Game Cards — horizontal scroll-snap track w/ dot stepper */}
              {!hasGroups ? (
                <MatchCarousel games={undercard.games} renderCard={renderUnifiedCard} />
              ) : (
                <div className="space-y-8">
                  {(() => {
                    const groupOrder: string[] = [];
                    const groupMap = new Map<string, GameData[]>();
                    const ungrouped: GameData[] = [];
                    for (const game of undercard.games) {
                      const g = game.group || '';
                      if (!g) { ungrouped.push(game); continue; }
                      if (!groupMap.has(g)) { groupOrder.push(g); groupMap.set(g, []); }
                      groupMap.get(g)!.push(game);
                    }
                    return (
                      <>
                        {groupOrder.map((groupName) => {
                          const games = groupMap.get(groupName)!;
                          return (
                            <div key={groupName}>
                              <span className={cn('text-[10px] font-bold tracking-[0.2em] uppercase block mb-4', REGION_COLORS[groupName] || 'text-warm-gray/60')}>
                                {groupName}
                              </span>
                              <MatchCarousel games={games} renderCard={renderUnifiedCard} />
                            </div>
                          );
                        })}
                        {ungrouped.length > 0 && (
                          <MatchCarousel games={ungrouped} renderCard={renderUnifiedCard} />
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Player props are now integrated into UnifiedGameCard */}

              {/* Standalone props */}
              {standalone.length > 0 && (
                <div className="space-y-3">
                  {standalone.map((prop) => {
                    const purchased = myProps.find((p) => p.undercardPropId === prop.id) ?? null;
                    return (
                      <StandalonePropCard
                        key={prop.id}
                        prop={prop}
                        purchased={purchased}
                        onPurchase={handlePurchaseProp}
                        disabled={propDisabledRoundWide || (atLimit && !purchased)}
                        purchasingPropId={purchasingPropId}
                      />
                    );
                  })}
                </div>
              )}

              {/* Round-wide manual predictions (no gameId — apply to whole undercard) */}
              {manualRoundWide.length > 0 && (
                <div className="rounded-xl border border-surface-3 bg-surface-2 overflow-hidden w-full">
                  <div className="px-4 py-2 bg-surface-3/50 border-b border-surface-3">
                    <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-warm-gray/70">
                      Round-wide Predictions
                      <span className="text-warm-gray/40 ml-2 normal-case tracking-normal font-normal">({manualRoundWide.length})</span>
                    </span>
                  </div>
                  <div className="p-2 space-y-0.5">
                    {manualRoundWide.map((prop) => (
                      <InlineManualPropRow
                        key={prop.id}
                        prop={prop}
                        gameSettled={false}
                        purchased={myProps.find((m) => m.undercardPropId === prop.id) ?? null}
                        onPurchase={handlePurchaseProp}
                        disabled={propDisabledRoundWide || (atLimit && !purchasedPropIds.has(prop.id))}
                        purchasingPropId={purchasingPropId}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="mt-10">
            <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Leaderboard
            </span>
            <div className="bg-surface-2 rounded-xl border border-surface-3 overflow-hidden">
              <div className="divide-y divide-surface-3">
                {leaderboard.map((entry) => (
                  <div key={entry.userId} className="flex items-center justify-between px-4 py-3 hover:bg-surface-3/50 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                        entry.rank === 1 ? 'bg-gold/20 text-gold' :
                        entry.rank === 2 ? 'bg-tertiary/20 text-cream' :
                        entry.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-surface-3 text-tertiary'
                      )}>
                        {entry.rank}
                      </span>
                      <span className="text-sm text-cream group-hover:text-gold transition-colors">{entry.username}</span>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-sm font-bold text-gold">{entry.totalScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Floating action bar — pinned just above the bottom tab bar */}
      {showActionBar && (
        <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 border-t border-surface-3 bg-surface/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:justify-end">
            {showClear && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearPicks}
                className="text-warm-gray hover:text-cream shrink-0"
              >
                Clear
              </Button>
            )}

            {showEnter && (
              <>
                {parentAccessType === 'CODE_REQUIRED' && (
                  <input
                    type="text"
                    placeholder="Enter access code"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    className="px-3 py-2 rounded-lg bg-surface-2 border border-surface-3 text-cream placeholder-warm-gray/50 focus:border-gold focus:outline-none font-mono text-sm flex-1 min-w-0 sm:flex-none sm:w-48"
                  />
                )}
                <Button
                  variant="coins"
                  size="lg"
                  onClick={handleEnterAndSave}
                  disabled={entering || (parentAccessType === 'CODE_REQUIRED' && !accessCode.trim())}
                  className="flex-1 sm:flex-none"
                >
                  {entering
                    ? 'Entering...'
                    : hasDraftPicks
                      ? `Enter & Save (${draftPicks.size})`
                      : parentEntryFee === 0 ? 'Enter Event' : `Enter (${parentEntryFee} coins)`}
                </Button>
              </>
            )}

            {showSignIn && (
              <Button
                variant="coins"
                size="lg"
                onClick={() => router.push('/login')}
                className="flex-1 sm:flex-none"
              >
                Sign in to Enter
              </Button>
            )}

            {showSave && (
              <Button
                variant="coins"
                size="lg"
                onClick={handleSubmitPicks}
                disabled={submitting || draftPicks.size === 0}
                className="flex-1 sm:flex-none"
              >
                {submitting ? 'Saving...' : `Save Picks (${draftPicks.size})`}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
